// Chronic-condition Rx suggestion engine — ported from user's
// jm_suggest_chronic.rules.js. Triggers on Known Case Of chips AND
// Primary / Secondary diagnosis free-text ("Both" policy). Overlaps with the
// existing 17-class dose push engine (CKD RAASi/SGLT2, HFrEF ARNI/MRA-HF,
// Statin core, PPI, Levothyroxine) are intentionally excluded — those are
// handled by the stricter lab-gated engines in doseAlgorithms.js.
//
// Every rule returns the same shape as suggestAllPushDrugs so GuidancePanel
// renders them with identical "Prescribe this" one-tap flow.

const norm = (v) => (v == null ? "" : String(v)).toLowerCase();

// Build a single haystack from Known Case chips + Primary Dx + Secondary Dx.
const dxHaystack = (ctx) => [
  Array.isArray(ctx.known) ? ctx.known.join(" ") : ctx.known,
  ctx.diagnosisPrimary,
  ctx.diagnosisSecondary,
].map(norm).join(" ");

const hasDx = (ctx, ...patterns) => {
  const src = dxHaystack(ctx);
  return patterns.some((p) => (p instanceof RegExp ? p.test(src) : src.includes(norm(p))));
};

const alreadyOn = (currentMeds, ...names) => {
  const src = (currentMeds || [])
    .map((m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`)
    .join(" ")
    .toLowerCase();
  return names.some((n) => src.includes(String(n).toLowerCase()));
};

const num = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : null; };

// Rx row template compatible with PatientDetail.jsx emptyRow().
const mkRx = (over) => ({
  brand_name: "", generic_name: "", composition: "",
  strength: "", dose: "", route: "Oral", dosage_form: "Tablet",
  frequency: "", timing: "", duration: "Ongoing — review at follow-up",
  quantity: "", instructions: "", notes: "", type: "chronic",
  ...over,
});

// Suggestion template — matches the shape used by push-cards in GuidancePanel.
const mkSug = ({ regimen, drug, dose, titration, rationale, cautions = [], citations = [], recommended = true, rx }) =>
  ({ regimen, drug, dose, titration, rationale, cautions, citations, recommended, rx });

// -----------------------------------------------------------------------------
// RESPIRATORY (Asthma / COPD)  ── NEW (not covered elsewhere)
// -----------------------------------------------------------------------------
export function suggestRespiratory(ctx) {
  const hasAsthma = hasDx(ctx, /\basthma\b/);
  const hasCOPD = hasDx(ctx, /\bcopd\b/, /chronic obstructive/);
  if (!hasAsthma && !hasCOPD) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "budesonide/formoterol", "formoterol", "fluticasone", "salmeterol", "vilanterol")) {
    suggestions.push(mkSug({
      regimen: "ICS / LABA — controller",
      drug: "Budesonide + Formoterol 200/6 MDI",
      dose: "2 puffs BD",
      titration: "Step-up if reliever use > 2×/week or exacerbation; step down after 3 months of good control.",
      rationale: "GINA/GOLD: ICS/LABA is first-line controller for persistent asthma or symptomatic COPD.",
      cautions: [
        "Teach inhaler technique and mouth rinsing after use to reduce oral candidiasis.",
        "Assess adherence & triggers before escalating therapy.",
      ],
      citations: ["GINA 2025", "GOLD 2024"],
      recommended: true,
      rx: mkRx({
        brand_name: "Foracort 200 Inhaler", generic_name: "Budesonide + Formoterol",
        composition: "Budesonide 200 mcg + Formoterol 6 mcg", strength: "200/6 mcg",
        dose: "2 puffs", route: "Inhalation", dosage_form: "Inhaler (MDI)",
        frequency: "BD (morning & night)",
        instructions: "2 puffs each dose. Rinse mouth after use. Use spacer if available.",
      }),
    }));
  }

  if (!alreadyOn(meds, "levosalbutamol", "salbutamol", "albuterol")) {
    suggestions.push(mkSug({
      regimen: "SABA — reliever inhaler",
      drug: "Levosalbutamol 50 mcg MDI",
      dose: "2 puffs SOS / PRN",
      titration: "Overuse (>2 canisters/month) flags poor control — step up controller therapy.",
      rationale: "GINA/GOLD: Short-acting β-agonist for acute bronchospasm relief.",
      cautions: [
        "Do not rely on SABA alone in persistent asthma — combine with ICS.",
        "Teach spacer use where appropriate.",
      ],
      citations: ["GINA acute symptom relief"],
      recommended: false,
      rx: mkRx({
        brand_name: "Levolin Inhaler", generic_name: "Levosalbutamol",
        composition: "Levosalbutamol 50 mcg", strength: "50 mcg",
        dose: "2 puffs", route: "Inhalation", dosage_form: "Inhaler (MDI)",
        frequency: "PRN (SOS)",
        instructions: "For breathlessness/wheeze. Seek review if using > 2×/week.",
      }),
    }));
  }

  if (hasCOPD && !alreadyOn(meds, "tiotropium", "glycopyrronium", "umeclidinium")) {
    suggestions.push(mkSug({
      regimen: "LAMA — long-acting bronchodilator",
      drug: "Tiotropium 18 mcg DPI",
      dose: "1 inhalation OD",
      titration: "Add to ICS/LABA if exacerbations persist (Triple therapy).",
      rationale: "GOLD: LAMA reduces exacerbation frequency & improves dyspnoea in symptomatic COPD.",
      cautions: [
        "Caution in significant urinary retention or narrow-angle glaucoma.",
        "Teach correct device use and cleaning.",
      ],
      citations: ["GOLD 2024"],
      recommended: true,
      rx: mkRx({
        brand_name: "Tiova Rotacap", generic_name: "Tiotropium",
        composition: "Tiotropium 18 mcg", strength: "18 mcg",
        dose: "1 rotacap", route: "Inhalation", dosage_form: "DPI (Rotacap)",
        frequency: "OD",
        instructions: "Once-daily inhalation. Keep dry; do not swallow the cap.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// GOUT / HYPERURICEMIA  ── NEW
// -----------------------------------------------------------------------------
export function suggestGout(ctx) {
  if (!hasDx(ctx, /\bgout\b/, /hyperuricemia/, /hyperuricaemia/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "allopurinol", "febuxostat")) {
    suggestions.push(mkSug({
      regimen: "First-line ULT",
      drug: "Allopurinol 100 mg",
      dose: "100 mg OD (target serum uric acid < 6 mg/dL)",
      titration: "Uptitrate by 100 mg every 2-4 weeks to target UA. Cap in CKD.",
      rationale: "ACR: First-line urate-lowering therapy in chronic gout / hyperuricemia.",
      cautions: [
        "Do NOT initiate during an acute flare — cover with prophylaxis (colchicine 0.5 mg OD/BD × 3-6 months).",
        "Screen HLA-B*5801 in South-East Asian / Han Chinese to reduce SJS risk.",
        "Adjust dose in eGFR < 60.",
      ],
      citations: ["ACR 2020 Gout"],
      recommended: true,
      rx: mkRx({
        brand_name: "Zyloric 100", generic_name: "Allopurinol",
        composition: "Allopurinol 100 mg", strength: "100 mg",
        dose: "100 mg", frequency: "OD",
        instructions: "Start low, titrate to target UA < 6. Prophylax with colchicine while titrating.",
      }),
    }));
  }

  if (alreadyOn(meds, "allopurinol") && !alreadyOn(meds, "febuxostat")) {
    suggestions.push(mkSug({
      regimen: "Alternative ULT (allopurinol intolerant)",
      drug: "Febuxostat 40 mg",
      dose: "40 mg OD",
      titration: "Titrate to 80 mg if UA target not met.",
      rationale: "Xanthine oxidase inhibitor alternative when allopurinol not tolerated.",
      cautions: [
        "Review CV risk profile & regulatory advisory (CARES trial).",
        "Monitor LFTs and uric acid levels.",
      ],
      citations: ["ACR 2020; CARES"],
      recommended: false,
      rx: mkRx({
        brand_name: "Zurig 40", generic_name: "Febuxostat",
        composition: "Febuxostat 40 mg", strength: "40 mg",
        dose: "40 mg", frequency: "OD",
        instructions: "Use with flare prophylaxis at initiation.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// OSTEOPOROSIS / FRAGILITY FRACTURE  ── NEW
// -----------------------------------------------------------------------------
export function suggestOsteoporosis(ctx) {
  if (!hasDx(ctx, /osteoporosis/, /fragility fracture/, /low bone mass/, /\bbmd\b/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "alendronate", "risedronate", "zoledronic", "ibandronate", "denosumab")) {
    suggestions.push(mkSug({
      regimen: "First-line anti-resorptive",
      drug: "Alendronate 70 mg",
      dose: "70 mg once weekly",
      titration: "Continue 3–5 years then reassess (consider drug holiday if low-moderate risk).",
      rationale: "Reduces vertebral & non-vertebral fractures in osteoporosis.",
      cautions: [
        "Take with 200 ml plain water on empty stomach. Remain upright ≥ 30 min. No food/drink after.",
        "Dental review before starting; screen for osteonecrosis risk.",
        "Avoid in severe renal impairment (eGFR < 35) or esophageal stricture.",
      ],
      citations: ["Endocrine Society Osteoporosis 2020"],
      recommended: true,
      rx: mkRx({
        brand_name: "Osteofos 70", generic_name: "Alendronate",
        composition: "Alendronate 70 mg", strength: "70 mg",
        dose: "70 mg", frequency: "Once weekly (same day)",
        instructions: "Empty stomach with 200 ml water. Remain upright 30 min. No food/drink/other meds for 30 min.",
      }),
    }));
  }

  if (!alreadyOn(meds, "calcium", "cholecalciferol", "vitamin d")) {
    suggestions.push(mkSug({
      regimen: "Bone-health foundation",
      drug: "Calcium 500 mg + Vit D3 250 IU",
      dose: "1 tab BD with meals",
      titration: "Aim total elemental calcium 1000-1200 mg/day (diet + supplement).",
      rationale: "Ensure adequate substrate for bone remodelling; foundational to all osteoporosis Rx.",
      cautions: [
        "Assess dietary calcium before high-dose supplements to avoid vascular calcification.",
        "Avoid concurrent iron / thyroxine within 2 h.",
      ],
      citations: ["IOF osteoporosis nutrition"],
      recommended: false,
      rx: mkRx({
        brand_name: "Shelcal 500", generic_name: "Calcium carbonate + Cholecalciferol",
        composition: "Calcium 500 mg + Vit D3 250 IU", strength: "500 mg / 250 IU",
        dose: "1 tablet", frequency: "BD (with meals)",
        instructions: "With food to improve absorption. Separate from iron/thyroxine by 2 h.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// DEPRESSION / ANXIETY (Psych)  ── NEW
// -----------------------------------------------------------------------------
export function suggestPsych(ctx) {
  const hasDep = hasDx(ctx, /depression/, /depressive disorder/, /dysthymia/);
  const hasAnx = hasDx(ctx, /\banxiety\b/, /panic disorder/, /\bgad\b/);
  if (!hasDep && !hasAnx) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "escitalopram", "sertraline", "fluoxetine", "paroxetine", "citalopram")) {
    suggestions.push(mkSug({
      regimen: "First-line SSRI",
      drug: "Escitalopram 5 mg",
      dose: "5-10 mg once daily",
      titration: "Start 5 mg × 7 days, then 10 mg OD. Review at 4-6 weeks for response.",
      rationale: "APA/NICE: SSRI first-line for depression & generalised anxiety.",
      cautions: [
        "Warn about initial GI upset, headache, sleep changes.",
        "Monitor for suicidality, especially early & in patients < 25 y.",
        "Check QT interval if > 20 mg/day. Beware serotonergic drug interactions.",
      ],
      citations: ["APA Depression 2019", "NICE Depression NG222"],
      recommended: true,
      rx: mkRx({
        brand_name: "Nexito 5", generic_name: "Escitalopram",
        composition: "Escitalopram 5 mg", strength: "5 mg",
        dose: "5 mg", frequency: "OD (night)",
        instructions: "Take at same time daily. Review at 4-6 weeks; do not stop abruptly.",
      }),
    }));
  }

  if (hasAnx && !alreadyOn(meds, "clonazepam", "alprazolam", "diazepam", "lorazepam")) {
    suggestions.push(mkSug({
      regimen: "Short-term anxiolytic",
      drug: "Clonazepam 0.25 mg",
      dose: "0.25 mg SOS / HS",
      titration: "Limit to 2 weeks; taper after SSRI takes effect.",
      rationale: "Bridge relief for severe anxiety / insomnia while SSRI reaches steady state.",
      cautions: [
        "AVOID in substance-use disorder, severe respiratory disease, or elderly (Beers).",
        "High dependence potential — cap duration at 2-4 weeks.",
        "Taper slowly if used > 2 weeks.",
      ],
      citations: ["Anxiety treatment guidelines"],
      recommended: false,
      rx: mkRx({
        brand_name: "Rivotril 0.25", generic_name: "Clonazepam",
        composition: "Clonazepam 0.25 mg", strength: "0.25 mg",
        dose: "0.25 mg", frequency: "HS / SOS", duration: "14 days maximum",
        instructions: "Short course only. Do not drive after dosing. Taper after 2 weeks.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// EPILEPSY  ── NEW
// -----------------------------------------------------------------------------
export function suggestEpilepsy(ctx) {
  if (!hasDx(ctx, /epilepsy/, /seizure/, /\bfits\b/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "levetiracetam", "valproate", "carbamazepin", "lamotrigine", "phenytoin")) {
    suggestions.push(mkSug({
      regimen: "Broad-spectrum AED — first-line",
      drug: "Levetiracetam 500 mg",
      dose: "250-500 mg BD",
      titration: "Start 250 mg BD × 2 weeks, then 500 mg BD. Target 1-3 g/day.",
      rationale: "Broad-spectrum antiepileptic effective for focal & generalised seizures with favourable interaction profile.",
      cautions: [
        "Adjust dose for renal function (halve dose if eGFR < 50).",
        "Monitor for mood/behaviour changes & irritability.",
        "AVOID abrupt discontinuation — always taper.",
      ],
      citations: ["ILAE Epilepsy Treatment 2022"],
      recommended: true,
      rx: mkRx({
        brand_name: "Levipil 500", generic_name: "Levetiracetam",
        composition: "Levetiracetam 500 mg", strength: "500 mg",
        dose: "500 mg", frequency: "BD",
        instructions: "Take at same times daily. Never stop suddenly. Report mood changes.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// MIGRAINE  ── NEW
// -----------------------------------------------------------------------------
export function suggestMigraine(ctx) {
  if (!hasDx(ctx, /migraine/, /chronic headache/, /vascular headache/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "propranolol", "topiramate", "amitriptyline", "flunarizine")) {
    suggestions.push(mkSug({
      regimen: "Prophylaxis (≥ 4 attacks/month)",
      drug: "Propranolol 20-40 mg",
      dose: "20-40 mg BD",
      titration: "Titrate over 4 weeks based on response & tolerability.",
      rationale: "First-line prophylaxis in episodic migraine without comorbid asthma / severe bradycardia / HF.",
      cautions: [
        "AVOID in asthma / COPD, severe bradycardia, decompensated HF.",
        "Watch for fatigue, cold extremities, sleep disturbance.",
      ],
      citations: ["AHS Migraine 2021"],
      recommended: true,
      rx: mkRx({
        brand_name: "Ciplar 40", generic_name: "Propranolol",
        composition: "Propranolol 40 mg", strength: "40 mg",
        dose: "40 mg", frequency: "BD",
        instructions: "Take at same times daily. Do not stop suddenly.",
      }),
    }));
  }

  if (!alreadyOn(meds, "naproxen", "ibuprofen", "sumatriptan", "rizatriptan")) {
    suggestions.push(mkSug({
      regimen: "Acute attack — NSAID",
      drug: "Naproxen 500 mg",
      dose: "500 mg PRN at onset",
      titration: "Limit to 10 days/month to prevent medication-overuse headache.",
      rationale: "Fast-onset NSAID for acute migraine when no NSAID contraindication.",
      cautions: [
        "Avoid in advanced CKD, active peptic ulcer, high GI bleeding risk.",
        "Co-prescribe gastroprotection in high-risk patients.",
      ],
      citations: ["Acute migraine treatment"],
      recommended: false,
      rx: mkRx({
        brand_name: "Naprosyn 500", generic_name: "Naproxen",
        composition: "Naproxen 500 mg", strength: "500 mg",
        dose: "500 mg", frequency: "PRN at onset", duration: "PRN — 10 days/month max",
        instructions: "Take with food at first sign of attack. Max 10 days a month.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// NEUROPATHY (usually diabetic)  ── NEW
// -----------------------------------------------------------------------------
export function suggestNeuropathy(ctx) {
  if (!hasDx(ctx, /neuropathy/, /neuropathic pain/, /burning feet/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "pregabalin", "gabapentin", "duloxetine", "amitriptyline")) {
    suggestions.push(mkSug({
      regimen: "Neuropathic pain — first-line",
      drug: "Pregabalin 75 mg",
      dose: "75 mg HS (may uptitrate to BD)",
      titration: "Increase by 75 mg every 3-7 days as tolerated. Max 300-600 mg/day.",
      rationale: "First-line for painful diabetic peripheral neuropathy per ADA/AAN.",
      cautions: [
        "Adjust dose in CKD (halve dose if eGFR < 60).",
        "Monitor for dizziness, oedema, weight gain.",
        "Taper — do not discontinue abruptly.",
      ],
      citations: ["ADA DPN 2022", "AAN Painful DPN 2022"],
      recommended: true,
      rx: mkRx({
        brand_name: "Lyrica 75", generic_name: "Pregabalin",
        composition: "Pregabalin 75 mg", strength: "75 mg",
        dose: "75 mg", frequency: "HS (night)",
        instructions: "Take at bedtime. Titrate upward if sleep-limiting pain persists.",
      }),
    }));
  }

  if (!alreadyOn(meds, "duloxetine")) {
    suggestions.push(mkSug({
      regimen: "SNRI alternative — especially with depression",
      drug: "Duloxetine 30 mg",
      dose: "30 mg morning (titrate to 60 mg)",
      titration: "Start 30 mg × 1 week then 60 mg OD.",
      rationale: "Evidence for painful DPN & useful when depression coexists.",
      cautions: [
        "Monitor BP, nausea, insomnia.",
        "Taper rather than abruptly stopping.",
      ],
      citations: ["AAN Painful DPN 2022"],
      recommended: false,
      rx: mkRx({
        brand_name: "Duzela 30", generic_name: "Duloxetine",
        composition: "Duloxetine 30 mg", strength: "30 mg",
        dose: "30 mg", frequency: "OD (morning)",
        instructions: "Take in the morning. Consider dose increase after 1 week.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// CHRONIC PAIN / OSTEOARTHRITIS  ── NEW
// -----------------------------------------------------------------------------
export function suggestChronicPain(ctx) {
  if (!hasDx(ctx, /osteoarthritis/, /oa knee/, /chronic low back pain/, /chronic pain/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "diclofenac gel", "diclofenac topical", "topical nsaid")) {
    suggestions.push(mkSug({
      regimen: "First-line for localised OA",
      drug: "Diclofenac 1% gel (topical NSAID)",
      dose: "Thin film to affected joint TDS",
      titration: "Continue as symptoms need; systemic NSAID absorption is minimal.",
      rationale: "OA guidelines prefer topical NSAID over oral to minimise GI/renal risk.",
      cautions: [
        "Avoid on broken/inflamed skin.",
        "Wash hands after application.",
      ],
      citations: ["ACR/OARSI OA 2019"],
      recommended: true,
      rx: mkRx({
        brand_name: "Voveran Emulgel 1%", generic_name: "Diclofenac topical",
        composition: "Diclofenac diethylamine 1.16%", strength: "1%",
        dose: "Thin film", route: "Topical", dosage_form: "Gel",
        frequency: "TDS (3× daily)",
        instructions: "Massage gently over affected joint. Wash hands after.",
      }),
    }));
  }

  if (!alreadyOn(meds, "paracetamol", "acetaminophen")) {
    suggestions.push(mkSug({
      regimen: "Basic oral analgesic",
      drug: "Paracetamol 500-650 mg",
      dose: "500-650 mg PRN",
      titration: "Max 3 g/day. Watch total dose if on OTC combinations.",
      rationale: "Adjunct in chronic pain / OA with attention to total daily dose.",
      cautions: [
        "Cap ≤ 3 g/day in adults; ≤ 2 g/day if chronic alcohol use or liver disease.",
        "Screen for combination products (cough/cold syrups) that also contain paracetamol.",
      ],
      citations: ["Chronic pain guidance"],
      recommended: false,
      rx: mkRx({
        brand_name: "Dolo 650", generic_name: "Paracetamol",
        composition: "Paracetamol 650 mg", strength: "650 mg",
        dose: "650 mg", frequency: "PRN (up to QID)", duration: "PRN",
        instructions: "Do NOT exceed 3 g in 24 h. Check other products for paracetamol.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// PCOS / METABOLIC INFERTILITY  ── NEW (fertility-gated)
// -----------------------------------------------------------------------------
export function suggestPCOS(ctx) {
  if (!hasDx(ctx, /\bpcos\b/, /polycystic ovary/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const bmi = num(ctx.bmi);
  const fertility = hasDx(ctx, /fertility/, /infertility/, /conception/, /anovulation/);
  const suggestions = [];

  if (!fertility && !alreadyOn(meds, "cocp", "ethinyl estradiol", "drospirenone", "dienogest")) {
    suggestions.push(mkSug({
      regimen: "Cycle regulation & hyperandrogenism (non-fertility goal)",
      drug: "Ethinyl estradiol 30 mcg + Drospirenone 3 mg (COCP)",
      dose: "1 tablet OD × 21 days, 7-day break",
      titration: "Reassess symptoms & side-effects at 3 months.",
      rationale: "Low-dose COCP with anti-androgenic progestin for menstrual regulation, acne, hirsutism.",
      cautions: [
        "AVOID in migraine with aura, uncontrolled HTN, VTE history, smokers > 35 y.",
        "Screen BP & lipid profile at baseline & 3 months.",
      ],
      citations: ["International PCOS Guidelines 2023"],
      recommended: true,
      rx: mkRx({
        brand_name: "Yasmin", generic_name: "Ethinyl estradiol + Drospirenone",
        composition: "Ethinyl estradiol 30 mcg + Drospirenone 3 mg", strength: "30 mcg / 3 mg",
        dose: "1 tablet", frequency: "OD × 21 days, 7 day break", duration: "3 months, review",
        instructions: "Same time daily. 21 active tabs then 7-day break. Use back-up if missed doses.",
      }),
    }));
  }

  if ((bmi == null || bmi >= 25) && !alreadyOn(meds, "metformin")) {
    suggestions.push(mkSug({
      regimen: "Metabolic — insulin resistance",
      drug: "Metformin ER 500 mg",
      dose: "500 mg with dinner (titrate to 1500-2000 mg/day)",
      titration: "Increase by 500 mg every 1-2 weeks based on GI tolerance.",
      rationale: "Insulin sensitiser — reduces cardiometabolic risk & may improve ovulation.",
      cautions: [
        "Monitor B12 annually on long-term therapy.",
        "Hold in dehydration / IV contrast / acute illness.",
      ],
      citations: ["International PCOS Guidelines 2023"],
      recommended: bmi != null && bmi >= 30,
      rx: mkRx({
        brand_name: "Glycomet SR 500", generic_name: "Metformin ER",
        composition: "Metformin extended release 500 mg", strength: "500 mg",
        dose: "500 mg", frequency: "OD (with dinner) — titrate BD/TDS",
        instructions: "Take with the largest meal. Increase gradually.",
      }),
    }));
  }

  if (fertility && !alreadyOn(meds, "letrozole", "clomiphene")) {
    suggestions.push(mkSug({
      regimen: "Ovulation induction (fertility goal)",
      drug: "Letrozole 2.5 mg",
      dose: "2.5 mg OD, cycle days 3-7",
      titration: "Titrate up to 7.5 mg in subsequent cycles if no ovulatory response.",
      rationale: "First-line oral ovulation induction agent — higher live-birth rate than clomiphene in PCOS.",
      cautions: [
        "SPECIALIST-GUIDED with ultrasound monitoring of follicular development.",
        "Rule out pregnancy before each cycle.",
      ],
      citations: ["International PCOS Guidelines 2023"],
      recommended: true,
      rx: mkRx({
        brand_name: "Femara 2.5", generic_name: "Letrozole",
        composition: "Letrozole 2.5 mg", strength: "2.5 mg",
        dose: "2.5 mg", frequency: "OD × cycle days 3-7", duration: "5 days per cycle",
        instructions: "Start on cycle day 3. Follicular scan on day 10-12. Specialist-supervised.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// BPH / LUTS  ── NEW
// -----------------------------------------------------------------------------
export function suggestBPH(ctx) {
  if (!hasDx(ctx, /\bbph\b/, /benign prostatic hyperplasia/, /\bluts\b/, /lower urinary tract/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "tamsulosin", "silodosin", "alfuzosin", "prazosin")) {
    suggestions.push(mkSug({
      regimen: "Alpha-1 blocker — first-line",
      drug: "Tamsulosin 0.4 mg",
      dose: "0.4 mg after dinner",
      titration: "Review symptoms in 4-6 weeks (IPSS).",
      rationale: "Improves flow & LUTS in moderate-severe BPH.",
      cautions: [
        "Postural hypotension, especially first dose — warn patient.",
        "Retrograde ejaculation is common.",
        "Inform ophthalmologist before cataract surgery (floppy iris syndrome).",
      ],
      citations: ["AUA BPH 2023"],
      recommended: true,
      rx: mkRx({
        brand_name: "Urimax 0.4", generic_name: "Tamsulosin",
        composition: "Tamsulosin 0.4 mg", strength: "0.4 mg",
        dose: "0.4 mg", frequency: "OD (after dinner)",
        instructions: "Take after dinner to reduce postural symptoms. Take at same time daily.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// ATRIAL FIBRILLATION (AF)  ── NEW
// -----------------------------------------------------------------------------
export function suggestAF(ctx) {
  if (!hasDx(ctx, /atrial fibrillation/, /\bafib\b/, /\baf\b/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "apixaban", "rivaroxaban", "dabigatran", "edoxaban", "warfarin")) {
    suggestions.push(mkSug({
      regimen: "Stroke prevention — DOAC",
      drug: "Apixaban 5 mg",
      dose: "5 mg BD (2.5 mg BD if ≥ 2 of: age ≥ 80, weight ≤ 60 kg, creatinine ≥ 1.5)",
      titration: "Confirm CHA₂DS₂-VASc ≥ 2 (men) / ≥ 3 (women) before starting.",
      rationale: "First-line stroke prevention in non-valvular AF.",
      cautions: [
        "AVOID in mechanical valves & moderate-severe mitral stenosis (warfarin preferred).",
        "Adjust dose for age / weight / creatinine.",
        "Check HAS-BLED and correct modifiable bleeding risks.",
      ],
      citations: ["ACC/AHA AF 2023", "ESC AF 2020"],
      recommended: true,
      rx: mkRx({
        brand_name: "Eliquis 5", generic_name: "Apixaban",
        composition: "Apixaban 5 mg", strength: "5 mg",
        dose: "5 mg", frequency: "BD",
        instructions: "Take at same times daily. Report any bleeding immediately.",
      }),
    }));
  }

  if (!alreadyOn(meds, "metoprolol", "bisoprolol", "carvedilol", "atenolol", "diltiazem", "verapamil")) {
    suggestions.push(mkSug({
      regimen: "Rate control",
      drug: "Metoprolol succinate 25 mg ER",
      dose: "25-50 mg OD (target resting HR < 110, symptom-relief HR 60-80)",
      titration: "Uptitrate every 1-2 weeks.",
      rationale: "Beta-blocker for rate control in AF where no contraindication.",
      cautions: [
        "AVOID in severe asthma, decompensated HF, marked bradycardia (< 50).",
        "Monitor BP & pulse after initiation.",
      ],
      citations: ["ACC/AHA AF 2023"],
      recommended: false,
      rx: mkRx({
        brand_name: "Metolar XR 25", generic_name: "Metoprolol succinate",
        composition: "Metoprolol succinate ER 25 mg", strength: "25 mg",
        dose: "25 mg", frequency: "OD",
        instructions: "Do not stop suddenly. Report dizziness or HR < 55.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// NAFLD / CHRONIC LIVER DISEASE  ── NEW
// -----------------------------------------------------------------------------
export function suggestLiverMetabolic(ctx) {
  const hasNAFLD = hasDx(ctx, /nafld/, /\bnash\b/, /fatty liver/);
  const hasCLD = hasDx(ctx, /\bcld\b/, /cirrhosis/, /chronic liver disease/, /portal hypertension/, /hepatic encephalopathy/);
  if (!hasNAFLD && !hasCLD) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const isDM = hasDx(ctx, /diabet/, /\bt2dm\b/);
  const suggestions = [];

  if (hasNAFLD && !isDM && !alreadyOn(meds, "vitamin e", "tocopherol")) {
    suggestions.push(mkSug({
      regimen: "Non-diabetic biopsy-proven NASH",
      drug: "Vitamin E 400 IU",
      dose: "400 IU OD",
      titration: "Reassess LFTs at 6 months.",
      rationale: "PIVENS: Vitamin E benefits non-diabetic biopsy-proven NASH.",
      cautions: [
        "Do NOT use in diabetics without specialist input (uncertain benefit).",
        "Long-term safety uncertain — periodically review.",
      ],
      citations: ["AASLD NAFLD 2023"],
      recommended: false,
      rx: mkRx({
        brand_name: "Evion 400", generic_name: "Vitamin E (Tocopherol)",
        composition: "Alpha-tocopherol 400 IU", strength: "400 IU",
        dose: "400 IU", frequency: "OD",
        instructions: "Take with a fatty meal for absorption.",
      }),
    }));
  }

  if (hasCLD && !alreadyOn(meds, "lactulose")) {
    suggestions.push(mkSug({
      regimen: "Hepatic encephalopathy prophylaxis",
      drug: "Lactulose syrup",
      dose: "15-30 ml BD/TDS (titrate to 2-3 soft stools/day)",
      titration: "Uptitrate stool frequency; avoid dehydration & hypokalemia.",
      rationale: "Reduces ammonia absorption & prevents/treats HE.",
      cautions: [
        "Combine with dietary protein optimisation per hepatologist.",
        "Watch for volume/electrolyte imbalance.",
      ],
      citations: ["AASLD HE 2023"],
      recommended: true,
      rx: mkRx({
        brand_name: "Duphalac", generic_name: "Lactulose",
        composition: "Lactulose 10 g/15 ml", strength: "10 g/15 ml", dosage_form: "Syrup",
        dose: "15-30 ml", frequency: "BD/TDS",
        instructions: "Titrate to 2-3 soft stools a day. Take with plenty of water.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// HEMATOLOGY (Iron / B12 deficiency anemia)  ── NEW
// -----------------------------------------------------------------------------
export function suggestHematology(ctx) {
  if (!hasDx(ctx, /anemia/, /anaemia/, /iron deficiency/, /megaloblastic/, /\bida\b/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "ferrous ascorbate", "ferrous sulfate", "iron")) {
    suggestions.push(mkSug({
      regimen: "Iron replacement",
      drug: "Ferrous ascorbate 100 mg (elemental Fe)",
      dose: "100 mg OD",
      titration: "Re-check Hb, MCV, ferritin, TSAT after 8-12 weeks.",
      rationale: "First-line therapy for iron-deficiency anemia while evaluating underlying etiology.",
      cautions: [
        "Take on empty stomach with vitamin C for absorption; alternate-day dosing improves absorption.",
        "Warn about black stools & constipation; add stool softener if needed.",
        "AVOID within 2 h of thyroxine / calcium / PPIs.",
      ],
      citations: ["BSH Iron Deficiency 2021"],
      recommended: true,
      rx: mkRx({
        brand_name: "Orofer XT", generic_name: "Ferrous ascorbate + Folic acid",
        composition: "Ferrous ascorbate 100 mg + Folic acid 1.5 mg", strength: "100 mg / 1.5 mg",
        dose: "1 tablet", frequency: "OD (empty stomach)",
        instructions: "Take 1 h before or 2 h after food. Best with vitamin C (orange juice).",
      }),
    }));
  }

  if (!alreadyOn(meds, "mecobalamin", "methylcobalamin", "cyanocobalamin", "vitamin b12")) {
    suggestions.push(mkSug({
      regimen: "B12 / Neurotropic support",
      drug: "Mecobalamin 1500 mcg + Folic acid + B6",
      dose: "1 tab OD",
      titration: "Consider parenteral B12 if severe deficiency or neuropathy.",
      rationale: "Supportive therapy in megaloblastic anemia / neuropathy — especially in vegetarian diet, metformin use, chronic gastritis.",
      cautions: [
        "Check baseline B12 & homocysteine when possible to guide duration.",
      ],
      citations: ["B12/folate deficiency guidelines"],
      recommended: false,
      rx: mkRx({
        brand_name: "Nurokind Plus", generic_name: "Mecobalamin + Folic acid + Pyridoxine",
        composition: "Mecobalamin 1500 mcg + Folic 1.5 mg + Pyridoxine 3 mg", strength: "1500 mcg complex",
        dose: "1 tablet", frequency: "OD",
        instructions: "Take with food. Continue at least 3 months.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// DERMATOLOGIC (Eczema / Psoriasis)  ── NEW
// -----------------------------------------------------------------------------
export function suggestDermatology(ctx) {
  const hasEczema = hasDx(ctx, /eczema/, /atopic dermatitis/);
  const hasPsoriasis = hasDx(ctx, /psoriasis/, /psoriatic/);
  if (!hasEczema && !hasPsoriasis) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "emollient", "moisturiser", "ceramide")) {
    suggestions.push(mkSug({
      regimen: "Foundation — barrier repair",
      drug: "Ceramide-based moisturiser",
      dose: "Liberal application, ≥ BID",
      titration: "Apply within 3 min of bathing to lock in hydration.",
      rationale: "Restores skin barrier; foundational for both eczema & psoriasis and reduces need for active agents.",
      cautions: [
        "Avoid perfumed / irritant products.",
        "Apply generously — 250-500 g/week for an adult with widespread eczema.",
      ],
      citations: ["AAD Atopic Dermatitis 2023"],
      recommended: true,
      rx: mkRx({
        brand_name: "Ceradan / Atopiclair / QV Cream", generic_name: "Ceramide moisturiser",
        composition: "Ceramide-based emollient", strength: "—",
        dose: "Liberal", route: "Topical", dosage_form: "Cream",
        frequency: "Multiple times/day",
        instructions: "Apply within 3 min of bathing. Use fingertip units — 1 FTU per palm-size area.",
      }),
    }));
  }

  if (!alreadyOn(meds, "mometasone", "betamethasone", "clobetasol", "hydrocortisone")) {
    suggestions.push(mkSug({
      regimen: "Acute flare — Topical Corticosteroid",
      drug: "Mometasone 0.1% cream",
      dose: "Thin film OD × 7-14 days",
      titration: "Step down to TCI or emollient-only maintenance once controlled.",
      rationale: "Rapid inflammation control for acute flares.",
      cautions: [
        "AVOID prolonged use (> 4 weeks) on face / flexures — risk of atrophy, striae, telangiectasia.",
        "Use lowest effective potency for shortest duration.",
      ],
      citations: ["AAD TCS Stewardship"],
      recommended: false,
      rx: mkRx({
        brand_name: "Elocon Cream", generic_name: "Mometasone furoate",
        composition: "Mometasone 0.1%", strength: "0.1%",
        dose: "Thin film", route: "Topical", dosage_form: "Cream",
        frequency: "OD", duration: "7-14 days",
        instructions: "Apply thinly to affected areas only. Do not cover with occlusive dressings unless advised.",
      }),
    }));
  }

  if (hasPsoriasis && !alreadyOn(meds, "calcipotriol", "calcipotriene")) {
    suggestions.push(mkSug({
      regimen: "Plaque psoriasis — combination",
      drug: "Calcipotriol + Betamethasone",
      dose: "Thin film OD × up to 4 weeks",
      titration: "Continue if response; step down to Vit D analogue alone for maintenance.",
      rationale: "First-line topical combination for chronic plaque psoriasis — dual anti-inflammatory & anti-proliferative.",
      cautions: [
        "AVOID on face, skin folds, or > 30% BSA.",
        "Cap 4 weeks continuous use of the combination.",
      ],
      citations: ["AAD Psoriasis 2020"],
      recommended: true,
      rx: mkRx({
        brand_name: "Daivobet", generic_name: "Calcipotriol + Betamethasone",
        composition: "Calcipotriol 50 mcg/g + Betamethasone 0.5 mg/g", strength: "50/500 mcg/g",
        dose: "Thin film", route: "Topical", dosage_form: "Ointment",
        frequency: "OD", duration: "≤ 4 weeks",
        instructions: "Apply thinly to plaques only. Avoid face and folds.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// OBESITY / METABOLIC SYNDROME (Orlistat only — GLP-1 covered elsewhere) ── NEW
// -----------------------------------------------------------------------------
export function suggestObesity(ctx) {
  const bmi = num(ctx.bmi);
  const hasObesityDx = hasDx(ctx, /obesity/, /overweight/, /metabolic syndrome/);
  if (!hasObesityDx && (bmi == null || bmi < 30)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "orlistat")) {
    suggestions.push(mkSug({
      regimen: "Anti-obesity pharmacotherapy",
      drug: "Orlistat 120 mg",
      dose: "120 mg with each main meal (or up to 1 h after)",
      titration: "Continue if ≥ 5% weight loss at 3 months.",
      rationale: "Lipase inhibitor for BMI ≥ 30 (or ≥ 27 with comorbidities).",
      cautions: [
        "GI side-effects: steatorrhoea, urgency — counsel on low-fat diet.",
        "AVOID in chronic malabsorption or cholestasis.",
        "Supplement fat-soluble vitamins (A, D, E, K) if long-term.",
      ],
      citations: ["Obesity pharmacotherapy guidance"],
      recommended: bmi != null && bmi >= 30,
      rx: mkRx({
        brand_name: "Xenical 120", generic_name: "Orlistat",
        composition: "Orlistat 120 mg", strength: "120 mg",
        dose: "120 mg", frequency: "TDS (with meals)",
        instructions: "Take with each fat-containing main meal. Follow reduced-fat diet.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// HYPERTHYROIDISM (Carbimazole — hypothyroid Levothyroxine covered elsewhere) ── NEW
// -----------------------------------------------------------------------------
export function suggestHyperthyroid(ctx) {
  if (!hasDx(ctx, /hyperthyroid/, /graves/, /thyrotoxicosis/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "carbimazole", "methimazole", "propylthiouracil")) {
    suggestions.push(mkSug({
      regimen: "Antithyroid — first-line",
      drug: "Carbimazole 10-20 mg",
      dose: "Divided doses per FT4 / body weight",
      titration: "Reduce dose as FT4 normalises. Typical duration 12-18 months.",
      rationale: "Thionamide of choice for Graves / hyperthyroidism per endocrine guidelines.",
      cautions: [
        "Monitor CBC & LFTs for agranulocytosis and hepatotoxicity.",
        "TEACH patient: report sore throat, fever, jaundice IMMEDIATELY — stop drug pending review.",
        "AVOID in 1st trimester of pregnancy — use PTU instead.",
        "Add propranolol for symptom control (tremor, palpitations).",
      ],
      citations: ["ATA Hyperthyroidism 2016"],
      recommended: true,
      rx: mkRx({
        brand_name: "Neo-Mercazole 5", generic_name: "Carbimazole",
        composition: "Carbimazole 5 mg", strength: "5 mg (2-4 tabs OD/BD)",
        dose: "10-20 mg", frequency: "OD or BD",
        instructions: "Take at same time daily. STOP and seek review if sore throat/fever/jaundice.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// RHEUMATOLOGY BIOLOGICS (RA / AS / SLE / PsA)  ── NEW
// -----------------------------------------------------------------------------
export function suggestRheumatologyBiologic(ctx) {
  const isRA = hasDx(ctx, /rheumatoid/, /\bra\b/);
  const isAS = hasDx(ctx, /ankylosing/, /spondylitis/, /hla-b27/);
  const isSLE = hasDx(ctx, /lupus/, /\bsle\b/);
  const isPsA = hasDx(ctx, /psoriatic arthritis/);
  if (!isRA && !isAS && !isSLE && !isPsA) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  // Pre-biologic screening ALWAYS shown
  suggestions.push(mkSug({
    regimen: "Pre-Biologic Safety Bundle — MANDATORY",
    drug: "TB / HBV / HCV / VZV screen + CXR",
    dose: "Once — before any biologic",
    titration: "Repeat TB screening annually.",
    rationale: "Screens for latent infections that can reactivate with immunosuppression.",
    cautions: [
      "Quantiferon-TB Gold + CXR.",
      "HBsAg + Anti-HBc + Anti-HCV.",
      "Varicella-Zoster IgG.",
      "Update inactivated vaccines (influenza, pneumococcal) BEFORE starting biologic.",
    ],
    citations: ["EULAR RA 2022"],
    recommended: true,
    rx: mkRx({
      brand_name: "Pre-biologic screening panel", generic_name: "Screening bundle",
      composition: "Quantiferon-TB + HBsAg + Anti-HCV + VZV IgG + CXR", strength: "—",
      dose: "—", route: "—", dosage_form: "Investigation",
      frequency: "Once + annual TB", duration: "Baseline",
      instructions: "Complete BEFORE initiating any biologic or JAK inhibitor.",
    }),
  }));

  if (isRA && !alreadyOn(meds, "methotrexate")) {
    suggestions.push(mkSug({
      regimen: "csDMARD — first-line RA",
      drug: "Methotrexate 7.5-15 mg + Folic acid",
      dose: "7.5-15 mg once weekly + Folic acid 5 mg (24 h after MTX)",
      titration: "Uptitrate MTX by 2.5-5 mg every 2-4 weeks to target 15-25 mg/week.",
      rationale: "Anchor csDMARD — must fail before stepping to biologic per ACR/EULAR.",
      cautions: [
        "AVOID in pregnancy (teratogen), heavy alcohol use, severe hepatic/renal disease.",
        "Monitor CBC, LFTs, creatinine every 2-4 weeks initially, then every 3 months.",
        "Effective contraception required in men and women of reproductive age.",
      ],
      citations: ["ACR RA 2021", "EULAR RA 2022"],
      recommended: true,
      rx: mkRx({
        brand_name: "Folitrax 10", generic_name: "Methotrexate",
        composition: "Methotrexate 10 mg", strength: "10 mg",
        dose: "10 mg", frequency: "Once weekly (same day)", duration: "Ongoing — review 3-monthly",
        instructions: "Take on the SAME DAY each week. Folic acid 5 mg the day after MTX. Alcohol strictly limited.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// CHRONIC VENOUS INSUFFICIENCY  ── NEW
// -----------------------------------------------------------------------------
export function suggestVenous(ctx) {
  if (!hasDx(ctx, /venous insufficiency/, /\bcvi\b/, /varicose veins/, /venous ulcer/)) {
    return { recommend: false, suggestions: [] };
  }
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "diosmin", "hesperidin", "micronised purified flavonoid")) {
    suggestions.push(mkSug({
      regimen: "Venoactive therapy",
      drug: "Diosmin 450 mg + Hesperidin 50 mg",
      dose: "1 tab BD × 2-3 months",
      titration: "Reassess symptoms & compression tolerance at 3 months.",
      rationale: "Reduces leg heaviness, cramps and oedema; adjunct to compression.",
      cautions: [
        "Adjunct to — NOT replacement for — compression stockings.",
        "Monitor for GI upset & rare hypersensitivity.",
      ],
      citations: ["CVI symptom management guidance"],
      recommended: false,
      rx: mkRx({
        brand_name: "Daflon 500", generic_name: "Diosmin + Hesperidin",
        composition: "Micronised purified flavonoid fraction 500 mg", strength: "500 mg",
        dose: "1 tablet", frequency: "BD",
        instructions: "Take with meals. Combine with graduated compression stockings & leg elevation.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// CHRONIC PANCREATITIS  ── NEW
// -----------------------------------------------------------------------------
export function suggestChronicPancreatitis(ctx) {
  if (!hasDx(ctx, /chronic pancreatitis/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];

  if (!alreadyOn(meds, "pancreatin", "pancreatic enzyme", "pancrelipase", "creon")) {
    suggestions.push(mkSug({
      regimen: "Pancreatic exocrine replacement",
      drug: "Pancreatin 25,000 IU (lipase)",
      dose: "1-2 caps with each main meal, 1 cap with snacks",
      titration: "Uptitrate to control steatorrhoea & maintain weight.",
      rationale: "PERT corrects exocrine insufficiency & steatorrhoea in chronic pancreatitis.",
      cautions: [
        "Take WITH the meal — not before or after.",
        "Monitor weight, stool character, fat-soluble vitamins.",
      ],
      citations: ["UEG Chronic Pancreatitis 2017"],
      recommended: true,
      rx: mkRx({
        brand_name: "Creon 25000", generic_name: "Pancreatin",
        composition: "Pancreatin 25,000 IU lipase", strength: "25,000 IU",
        dose: "1-2 capsules", frequency: "TDS (with meals)",
        instructions: "Swallow whole with cold food/water. Do not crush. Take with every meal.",
      }),
    }));
  }

  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// Master aggregator ── mirrors suggestAllPushDrugs shape.
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// BATCH 2 — user-requested conditions (Aug 2026)
// -----------------------------------------------------------------------------
export function suggestRecurrentUTI(ctx) {
  if (!hasDx(ctx, /recurrent uti/, /\bruti\b/, /recurrent urinary/, /\bcystitis\b/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "nitrofurantoin")) {
    suggestions.push(mkSug({
      regimen: "Recurrent / uncomplicated lower UTI", drug: "Nitrofurantoin 100 mg", dose: "100 mg BD for 5 days",
      titration: "Repeat urine culture if symptoms persist beyond 72 h.",
      rationale: "ICMR-AMR 2022: nitrofurantoin is first-line empiric therapy for uncomplicated cystitis.",
      cautions: ["Contraindicated if eGFR < 30 mL/min, anuria or term pregnancy.", "Send urine culture BEFORE starting."],
      citations: ["ICMR-AMR 2022 Ch.3"],
      rx: mkRx({ brand_name: "Niftas 100", generic_name: "Nitrofurantoin", strength: "100 mg", dose: "1 tab", frequency: "BD", timing: "After food", duration: "5 days" }),
    }));
  }
  if (!alreadyOn(meds, "fosfomycin")) {
    suggestions.push(mkSug({
      regimen: "Single-dose alternative", drug: "Fosfomycin 3 g sachet", dose: "3 g single dose",
      titration: "One-time dose; reassess with culture if no response in 48-72 h.",
      rationale: "ICMR-AMR 2022: single-dose fosfomycin is an alternative first-line for acute uncomplicated cystitis.",
      cautions: ["Avoid in severe renal impairment."], citations: ["ICMR-AMR 2022 Ch.3"], recommended: false,
      rx: mkRx({ brand_name: "Fosirol 3g", generic_name: "Fosfomycin", strength: "3 g", dose: "1 sachet in water", dosage_form: "Sachet", frequency: "Single dose", timing: "Empty stomach at bedtime", duration: "Once" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestBronchiectasis(ctx) {
  if (!hasDx(ctx, /bronchiectasis/)) return { recommend: false, suggestions: [] };
  const suggestions = [];
  if (!alreadyOn(ctx.currentMeds, "acetylcysteine")) {
    suggestions.push(mkSug({
      regimen: "Airway clearance support", drug: "Acetylcysteine 600 mg", dose: "600 mg OD-BD",
      titration: "Continue as maintenance mucolytic alongside daily chest physiotherapy.",
      rationale: "Reduces sputum viscosity and aids clearance in bronchiectasis maintenance.",
      cautions: ["Avoid in active peptic ulcer disease.", "Ensure annual influenza + pneumococcal vaccination."],
      citations: ["GOLD 2024"],
      rx: mkRx({ brand_name: "Mucinac 600", generic_name: "Acetylcysteine", strength: "600 mg", dose: "1 effervescent tab in water", frequency: "OD", timing: "After food" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestLiverPanel(ctx) {
  const ald = hasDx(ctx, /alcoholic liver/, /\bald\b/, /alcoholic hepatitis/);
  const hcv = hasDx(ctx, /hepatitis c/, /\bhcv\b/);
  const cirr = hasDx(ctx, /cirrhosis/);
  if (!ald && !hcv && !cirr) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (ald && !alreadyOn(meds, "thiamine")) {
    suggestions.push(mkSug({
      regimen: "ALD — nutritional rescue", drug: "Thiamine 100 mg + Folic acid 5 mg", dose: "One each OD",
      titration: "Continue while alcohol intake or malnutrition persists.",
      rationale: "Prophylaxis against Wernicke's encephalopathy; corrects folate deficiency of chronic alcohol use.",
      cautions: ["Absolute alcohol abstinence is the definitive treatment.", "Refer for de-addiction support."],
      citations: ["AASLD 2023"],
      rx: mkRx({ brand_name: "Benadon 100", generic_name: "Thiamine", strength: "100 mg", dose: "1 tab", frequency: "OD", timing: "After breakfast" }),
    }));
  }
  if (hcv && !alreadyOn(meds, "sofosbuvir", "velpatasvir")) {
    suggestions.push(mkSug({
      regimen: "Chronic HCV — curative DAA", drug: "Sofosbuvir 400 mg + Velpatasvir 100 mg", dose: "1 tab OD for 12 weeks",
      titration: "Check HCV RNA at week 12 post-treatment for SVR.",
      rationale: "Pangenotypic DAA regimen — curative for chronic hepatitis C.",
      cautions: ["NEVER co-administer with amiodarone (severe bradycardia).", "Baseline: HCV RNA, HBsAg, LFT, creatinine."],
      citations: ["AASLD-IDSA HCV Guidance"],
      rx: mkRx({ brand_name: "Velpanat", generic_name: "Sofosbuvir + Velpatasvir", strength: "400/100 mg", dose: "1 tab", frequency: "OD", duration: "12 weeks" }),
    }));
  }
  if (cirr) {
    if (!alreadyOn(meds, "spironolactone")) {
      suggestions.push(mkSug({
        regimen: "Cirrhosis — ascites (only if present)", drug: "Spironolactone 50 mg", dose: "50 mg OD",
        titration: "Titrate to weight loss 0.5 kg/day; add furosemide 40 mg if inadequate.",
        rationale: "First-line diuretic for cirrhotic ascites.",
        cautions: ["Check K+ and creatinine within 1 week.", "Avoid if eGFR < 30 or hyperkalaemia."],
        citations: ["AASLD 2023"], recommended: false,
        rx: mkRx({ brand_name: "Aldactone 50", generic_name: "Spironolactone", strength: "50 mg", dose: "1 tab", frequency: "OD", timing: "Morning" }),
      }));
    }
    if (!alreadyOn(meds, "propranolol", "carvedilol")) {
      suggestions.push(mkSug({
        regimen: "Variceal prophylaxis (only if varices on endoscopy)", drug: "Propranolol 20 mg", dose: "20 mg BD",
        titration: "Titrate to resting HR 55-60 bpm.",
        rationale: "Non-selective beta-blocker for primary prophylaxis of variceal bleeding.",
        cautions: ["Requires prior endoscopy confirming moderate/large varices.", "Avoid in asthma, heart block, refractory ascites."],
        citations: ["AASLD 2023"], recommended: false,
        rx: mkRx({ brand_name: "Ciplar 20", generic_name: "Propranolol", strength: "20 mg", dose: "1 tab", frequency: "BD" }),
      }));
    }
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestGallstoneStone(ctx) {
  const gall = hasDx(ctx, /gallstone/, /cholelithiasis/);
  const renal = hasDx(ctx, /nephrolithiasis/, /renal stone/, /kidney stone/, /ureteric stone/, /urolithiasis/);
  if (!gall && !renal) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (gall && !alreadyOn(meds, "ursodeoxycholic", "udiliv")) {
    suggestions.push(mkSug({
      regimen: "Gallstones — dissolution (select cases only)", drug: "UDCA 300 mg", dose: "300 mg BD",
      titration: "Reserve for small radiolucent cholesterol stones in a functioning gallbladder; review USG at 6 months.",
      rationale: "UDCA dissolution therapy for patients unfit for / declining cholecystectomy.",
      cautions: ["Symptomatic gallstones → surgical referral (lap cholecystectomy) is definitive.", "Not for calcified or pigment stones."],
      citations: ["Standard of Care"], recommended: false,
      rx: mkRx({ brand_name: "Udiliv 300", generic_name: "Ursodeoxycholic acid", strength: "300 mg", dose: "1 tab", frequency: "BD", timing: "After food" }),
    }));
  }
  if (renal) {
    if (!alreadyOn(meds, "potassium citrate", "cital")) {
      suggestions.push(mkSug({
        regimen: "Nephrolithiasis — medical therapy", drug: "Potassium citrate syrup", dose: "10 mL in a glass of water BD",
        titration: "Target urine pH 6.5-7 and urine volume > 2.5 L/day.",
        rationale: "Urinary alkalinisation and citrate repletion reduce stone recurrence.",
        cautions: ["Check serum potassium if CKD.", "24-h urine metabolic workup for recurrent stones."],
        citations: ["AUA Stone 2019"],
        rx: mkRx({ brand_name: "Cital Syrup", generic_name: "Potassium citrate", strength: "—", dose: "10 mL diluted", dosage_form: "Syrup", frequency: "BD", timing: "After food" }),
      }));
    }
    if (!alreadyOn(meds, "tamsulosin")) {
      suggestions.push(mkSug({
        regimen: "Ureteric stone 5-10 mm — expulsion therapy", drug: "Tamsulosin 0.4 mg", dose: "0.4 mg OD at night",
        titration: "Up to 4 weeks; refer urology if not passed or intractable pain/fever.",
        rationale: "Alpha-blocker medical expulsive therapy for distal ureteric stones.",
        cautions: ["Postural hypotension — take at bedtime.", "Fever + obstruction = urological emergency."],
        citations: ["AUA Stone 2019"], recommended: false,
        rx: mkRx({ brand_name: "Urimax 0.4", generic_name: "Tamsulosin", strength: "0.4 mg", dose: "1 cap", dosage_form: "Capsule", frequency: "OD", timing: "After dinner" }),
      }));
    }
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestEndocrineRare(ctx) {
  const addison = hasDx(ctx, /addison/, /adrenal insufficiency/);
  const prolact = hasDx(ctx, /hyperprolactin/, /prolactinoma/);
  const rhd = hasDx(ctx, /rheumatic heart/, /\brhd\b/, /rheumatic fever/);
  if (!addison && !prolact && !rhd) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (addison && !alreadyOn(meds, "hydrocortisone")) {
    suggestions.push(mkSug({
      regimen: "Addison's — replacement (endocrinologist-supervised)", drug: "Hydrocortisone", dose: "10 mg morning + 5 mg evening",
      titration: "Endocrinology titration; sick-day rule: double dose during febrile illness.",
      rationale: "Physiological glucocorticoid replacement for primary adrenal insufficiency.",
      cautions: ["Never stop abruptly — adrenal crisis risk.", "Educate on steroid card + injectable hydrocortisone for emergencies.", "May also need fludrocortisone (specialist)."],
      citations: ["Endocrine Society 2016"], recommended: false,
      rx: mkRx({ brand_name: "Hisone 10", generic_name: "Hydrocortisone", strength: "10 mg", dose: "10 mg AM + 5 mg PM", frequency: "BD", timing: "Morning & evening" }),
    }));
  }
  if (prolact && !alreadyOn(meds, "cabergoline")) {
    suggestions.push(mkSug({
      regimen: "Hyperprolactinaemia", drug: "Cabergoline 0.25 mg", dose: "0.25 mg twice weekly",
      titration: "Re-check prolactin at 4 weeks; titrate to normalisation. MRI pituitary if macroadenoma suspected.",
      rationale: "Dopamine agonist — first-line for symptomatic hyperprolactinaemia / prolactinoma.",
      cautions: ["Exclude pregnancy, hypothyroidism and drug-induced causes first.", "Echocardiogram for long-term high-dose use."],
      citations: ["Endocrine Society 2011"],
      rx: mkRx({ brand_name: "Cabgolin 0.25", generic_name: "Cabergoline", strength: "0.25 mg", dose: "1 tab", frequency: "Twice weekly", timing: "After food" }),
    }));
  }
  if (rhd && !alreadyOn(meds, "penicillin", "penidure")) {
    suggestions.push(mkSug({
      regimen: "RHD — secondary prophylaxis", drug: "Benzathine Penicillin G 1.2 MU", dose: "1.2 MU IM every 3-4 weeks",
      titration: "Continue per AHA/WHO duration rules (often until age 40 / lifelong if severe).",
      rationale: "Prevents recurrent rheumatic fever and progression of valve damage.",
      cautions: ["Test dose / anaphylaxis preparedness.", "Echo surveillance of valve lesions."],
      citations: ["AHA RHD 2020"],
      rx: mkRx({ brand_name: "Penidure LA 12", generic_name: "Benzathine Penicillin G", strength: "1.2 MU", dose: "1.2 MU deep IM", route: "IM", dosage_form: "Injection", frequency: "Every 3-4 weeks", duration: "Long-term per protocol" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestIBD(ctx) {
  if (!hasDx(ctx, /ulcerative colitis/, /crohn/, /\bibd\b/, /inflammatory bowel/)) return { recommend: false, suggestions: [] };
  const suggestions = [];
  if (!alreadyOn(ctx.currentMeds, "mesalamine", "mesalazine")) {
    suggestions.push(mkSug({
      regimen: "Ulcerative colitis — induction & maintenance", drug: "Mesalamine 1.2 g", dose: "1.2 g BD",
      titration: "Reassess at 4-6 weeks; escalate to GI specialist if no remission or Crohn's phenotype.",
      rationale: "5-ASA is first-line for mild-moderate ulcerative colitis.",
      cautions: ["Confirm diagnosis with colonoscopy + biopsy; exclude infective colitis first.", "Monitor renal function annually."],
      citations: ["ACG UC 2019"],
      rx: mkRx({ brand_name: "Mesacol 1.2g", generic_name: "Mesalamine", strength: "1.2 g", dose: "1 tab", frequency: "BD", timing: "After food" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestFibromyalgia(ctx) {
  if (!hasDx(ctx, /fibromyalgia/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "pregabalin")) {
    suggestions.push(mkSug({
      regimen: "Fibromyalgia — first-line neuromodulator", drug: "Pregabalin 75 mg", dose: "75 mg HS",
      titration: "Increase to 150 mg HS after 1-2 weeks if tolerated.",
      rationale: "Improves widespread pain and sleep disturbance in fibromyalgia.",
      cautions: ["Dose-adjust in renal impairment.", "Graded aerobic exercise is the core of therapy."],
      citations: ["EULAR Fibromyalgia 2017"],
      rx: mkRx({ brand_name: "Pregalin 75", generic_name: "Pregabalin", strength: "75 mg", dose: "1 cap", dosage_form: "Capsule", frequency: "HS", timing: "At bedtime" }),
    }));
  }
  if (!alreadyOn(meds, "duloxetine")) {
    suggestions.push(mkSug({
      regimen: "Alternative / fatigue-mood phenotype", drug: "Duloxetine 30 mg", dose: "30 mg OD",
      titration: "Increase to 60 mg after 2 weeks if needed.",
      rationale: "SNRI — useful when fibromyalgia coexists with fatigue or depression.",
      cautions: ["Avoid in hepatic disease, CrCl < 30, uncontrolled hypertension, MAOI use."],
      citations: ["EULAR Fibromyalgia 2017"], recommended: false,
      rx: mkRx({ brand_name: "Duzela 30", generic_name: "Duloxetine", strength: "30 mg", dose: "1 cap", dosage_form: "Capsule", frequency: "OD", timing: "After breakfast" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestNeuroDegen(ctx) {
  const pd = hasDx(ctx, /parkinson/);
  const dem = hasDx(ctx, /dementia/, /alzheimer/);
  if (!pd && !dem) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (pd && !alreadyOn(meds, "levodopa", "syndopa")) {
    suggestions.push(mkSug({
      regimen: "Parkinson's — symptomatic gold standard", drug: "Levodopa + Carbidopa 100/25", dose: "100/25 mg TDS",
      titration: "Neurology titration to motor response; take 30-60 min before protein-rich meals.",
      rationale: "Most effective symptomatic therapy for Parkinsonian motor symptoms.",
      cautions: ["Avoid abrupt withdrawal.", "Watch for dyskinesia and orthostatic hypotension.", "Fall-prevention assessment at home."],
      citations: ["AAN PD 2021"],
      rx: mkRx({ brand_name: "Syndopa 110", generic_name: "Levodopa + Carbidopa", strength: "100/25 mg", dose: "1 tab", frequency: "TDS", timing: "Before food" }),
    }));
  }
  if (dem && !alreadyOn(meds, "donepezil", "rivastigmine", "memantine")) {
    suggestions.push(mkSug({
      regimen: "Mild-moderate dementia", drug: "Donepezil 5 mg", dose: "5 mg HS",
      titration: "May increase to 10 mg after 4-6 weeks if tolerated.",
      rationale: "Cholinesterase inhibitor — first-line symptomatic therapy for cognitive symptoms.",
      cautions: ["Baseline ECG — avoid in bradycardia / sick sinus.", "Caution with peptic ulcer disease.", "Assess caregiver support."],
      citations: ["Standard Neurology Care"],
      rx: mkRx({ brand_name: "Donep 5", generic_name: "Donepezil", strength: "5 mg", dose: "1 tab", frequency: "HS", timing: "At bedtime" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestInsomnia(ctx) {
  if (!hasDx(ctx, /insomnia/, /sleep disorder/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "melatonin")) {
    suggestions.push(mkSug({
      regimen: "Insomnia — safest first-line agent", drug: "Melatonin 3 mg", dose: "3 mg 1-2 h before bedtime",
      titration: "Pair with strict sleep hygiene + CBT-I; review at 4 weeks.",
      rationale: "No dependence or hangover; preferred over sedative-hypnotics (Beers 2023).",
      cautions: ["CBT-I is the most effective long-term treatment.", "Limit caffeine after noon; fixed wake time."],
      citations: ["Beers 2023"],
      rx: mkRx({ brand_name: "Meloset 3", generic_name: "Melatonin", strength: "3 mg", dose: "1 tab", frequency: "HS", timing: "1-2 h before bedtime" }),
    }));
  }
  if (!alreadyOn(meds, "zolpidem")) {
    suggestions.push(mkSug({
      regimen: "Short-term rescue only", drug: "Zolpidem 5 mg", dose: "5 mg HS (max 2 weeks)",
      titration: "Strictly short-term; avoid renewal without review.",
      rationale: "Non-BZD hypnotic if melatonin + sleep hygiene fail.",
      cautions: ["Avoid in OSA, hepatic impairment, elderly (falls).", "Complex sleep behaviours — stop immediately if they occur."],
      citations: ["Beers 2023"], recommended: false,
      rx: mkRx({ brand_name: "Zolfresh 5", generic_name: "Zolpidem", strength: "5 mg", dose: "1 tab", frequency: "HS", duration: "Max 2 weeks" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export function suggestENTAllergy(ctx) {
  const sinus = hasDx(ctx, /chronic sinusitis/, /rhinosinusitis/);
  const ar = hasDx(ctx, /allergic rhinitis/);
  if (!sinus && !ar) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "fluticasone", "mometasone")) {
    suggestions.push(mkSug({
      regimen: sinus ? "Chronic sinusitis — controller" : "Allergic rhinitis — controller",
      drug: "Fluticasone nasal spray", dose: "2 sprays each nostril OD",
      titration: "Daily saline irrigation + review at 6-8 weeks.",
      rationale: "Intranasal corticosteroid is first-line controller; avoids inappropriate systemic antibiotics.",
      cautions: ["Antibiotics (Augmentin 625 BD 5-7d) ONLY for acute bacterial exacerbation.", "Teach correct spray technique (aim away from septum)."],
      citations: sinus ? ["ICMR-AMR 2022 Ch.3"] : ["ARIA 2024"],
      rx: mkRx({ brand_name: "Flomist Nasal Spray", generic_name: "Fluticasone", strength: "50 mcg/spray", dose: "2 sprays each nostril", route: "Intranasal", dosage_form: "Nasal Spray", frequency: "OD", timing: "Morning" }),
    }));
  }
  if (ar && !alreadyOn(meds, "bilastine", "fexofenadine", "levocetirizine")) {
    suggestions.push(mkSug({
      regimen: "Symptom relief — non-sedating antihistamine", drug: "Bilastine 20 mg", dose: "20 mg OD",
      titration: "Use during symptomatic periods; combine with allergen avoidance.",
      rationale: "Second-generation H1 blocker — non-sedating relief of histamine symptoms.",
      cautions: ["Take on empty stomach for best absorption."], citations: ["ARIA 2024"],
      rx: mkRx({ brand_name: "Bilaxten 20", generic_name: "Bilastine", strength: "20 mg", dose: "1 tab", frequency: "OD", timing: "Empty stomach" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// SPINE / SOFT-TISSUE PAIN (Cervical & Lumbar Spondylosis, Frozen Shoulder,
// Plantar Fasciitis) — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestSpinePain(ctx) {
  const cerv = hasDx(ctx, /cervical spondylosis/, /cervical radiculopathy/);
  const lumb = hasDx(ctx, /lumbar spondylosis/, /\blbp\b/, /low back ache/, /lumbago/, /sciatica/);
  const shoulder = hasDx(ctx, /frozen shoulder/, /adhesive capsulitis/, /periarthritis/);
  const plantar = hasDx(ctx, /plantar fasciitis/, /heel pain/);
  if (!cerv && !lumb && !shoulder && !plantar) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "aceclofenac", "diclofenac", "etoricoxib", "naproxen")) {
    suggestions.push(mkSug({
      regimen: "Short NSAID course — flare control",
      drug: "Aceclofenac 100 + Paracetamol 325", dose: "1 tab BD × 5-7 days",
      titration: "Shortest effective course; re-assess at 1 week. Escalate to physiotherapy referral, not longer NSAIDs.",
      rationale: "Symptomatic relief for mechanical spine/soft-tissue pain; exercise therapy is the definitive treatment.",
      cautions: ["Avoid in CKD (eGFR < 60), peptic ulcer, or on anticoagulants — use Paracetamol alone.", "Co-prescribe PPI if age > 65 or GI risk."],
      citations: ["NICE LBP NG59", "ICMR MSK 2023"],
      rx: mkRx({ brand_name: "Zerodol-P", generic_name: "Aceclofenac + Paracetamol", composition: "Aceclofenac 100 mg + Paracetamol 325 mg", strength: "100/325 mg", dose: "1 tab", frequency: "BD", timing: "After food", duration: "5-7 days", type: "acute" }),
    }));
  }
  suggestions.push(mkSug({
    regimen: "Topical NSAID — local relief",
    drug: "Diclofenac gel 1%", dose: "Apply thin layer TDS over affected area",
    titration: "Safe long-term adjunct; combine with heat + stretching.",
    rationale: "Topical NSAIDs give meaningful relief with minimal systemic exposure — first choice in elderly/CKD.",
    cautions: ["Do not combine with oral NSAID beyond 1 week without review."],
    citations: ["NICE NG59"],
    recommended: false,
    rx: mkRx({ brand_name: "Voveran Emulgel", generic_name: "Diclofenac (topical)", strength: "1% w/w", dose: "Thin layer", route: "Topical", dosage_form: "Gel", frequency: "TDS", duration: "2 weeks" }),
  }));
  if (shoulder) {
    suggestions.push(mkSug({
      regimen: "Frozen shoulder — mobilisation plan",
      drug: "Structured physiotherapy (pendular + capsular stretch)", dose: "Daily home programme + supervised weekly",
      titration: "If no gain at 6 weeks → intra-articular steroid injection (orthopaedic referral).",
      rationale: "Adhesive capsulitis resolves with graded mobilisation; delay worsens stiffness. Screen/optimise diabetes — strongly associated.",
      cautions: ["Check HbA1c — frozen shoulder is 5× more common with poorly controlled DM."],
      citations: ["AAOS 2024"],
      recommended: false,
    }));
  }
  if (plantar) {
    suggestions.push(mkSug({
      regimen: "Plantar fasciitis — mechanical unloading",
      drug: "Calf/plantar stretching + silicone heel cup", dose: "Stretch 3×/day; wear cushioned footwear",
      titration: "80% resolve in 6 months with stretching alone. Persistent → local steroid injection.",
      rationale: "First-line is stretching + footwear modification, not medication.",
      cautions: ["Screen for weight — BMI reduction reduces recurrence."],
      citations: ["AOFAS 2023"],
      recommended: false,
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// VERTIGO (BPPV) + TRIGEMINAL NEURALGIA — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestVertigoNeuralgia(ctx) {
  const vert = hasDx(ctx, /vertigo/, /\bbppv\b/, /meniere/, /labyrinthitis/);
  const tn = hasDx(ctx, /trigeminal neuralgia/);
  if (!vert && !tn) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (vert && !alreadyOn(meds, "betahistine", "cinnarizine")) {
    suggestions.push(mkSug({
      regimen: "Vestibular suppressant — symptomatic",
      drug: "Betahistine 16 mg", dose: "16 mg TDS × 2-4 weeks",
      titration: "Taper once episodes settle; long-term only for Ménière's. BPPV: perform/teach Epley manoeuvre — it is the definitive cure.",
      rationale: "Betahistine improves vestibular compensation; Epley repositioning resolves BPPV in 1-2 sessions.",
      cautions: ["Avoid in active peptic ulcer and phaeochromocytoma.", "Persistent vertigo with neuro signs → urgent imaging (rule out posterior circulation stroke)."],
      citations: ["Bárány Society 2023"],
      rx: mkRx({ brand_name: "Vertin 16", generic_name: "Betahistine", strength: "16 mg", dose: "1 tab", frequency: "TDS", timing: "After food", duration: "2-4 weeks" }),
    }));
  }
  if (tn && !alreadyOn(meds, "carbamazepin", "oxcarbazepin")) {
    suggestions.push(mkSug({
      regimen: "Trigeminal neuralgia — first line",
      drug: "Carbamazepine 100 mg", dose: "100 mg BD, titrate weekly by 100-200 mg (usual 400-800 mg/day)",
      titration: "Increase until pain-free or side-effects; attempt slow taper after 6-8 pain-free weeks.",
      rationale: "Carbamazepine is the only first-line drug with strong evidence in TN (NNT ≈ 1.8).",
      cautions: ["Baseline CBC, LFT, Na — repeat at 2 & 8 weeks (hyponatraemia, marrow suppression).", "Serious skin reactions (SJS) risk — counsel to stop at first rash.", "Multiple drug interactions (enzyme inducer) — check DDI before adding."],
      citations: ["AAN/EFNS TN Guideline"],
      rx: mkRx({ brand_name: "Tegrital 100", generic_name: "Carbamazepine", strength: "100 mg", dose: "1 tab", frequency: "BD", timing: "After food", instructions: "Increase weekly as advised. Report any skin rash immediately." }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// POST-CVA SECONDARY PREVENTION — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestPostCVA(ctx) {
  if (!hasDx(ctx, /post cva/, /\bstroke\b/, /\btia\b/, /hemiparesis/, /cerebrovascular accident/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  const onAnticoag = alreadyOn(meds, "warfarin", "apixaban", "rivaroxaban", "dabigatran", "acitrom", "acenocoumarol");
  if (!onAnticoag && !alreadyOn(meds, "aspirin", "clopidogrel", "ecosprin")) {
    suggestions.push(mkSug({
      regimen: "Antiplatelet — secondary prevention",
      drug: "Aspirin 75 mg", dose: "75 mg OD after food",
      titration: "Lifelong for ischaemic stroke/TIA unless anticoagulated for AF (then anticoagulant replaces antiplatelet).",
      rationale: "Antiplatelet therapy cuts recurrent ischaemic stroke risk by ~25%.",
      cautions: ["CONFIRM the stroke was ischaemic (not haemorrhagic) before starting.", "If AF is present, anticoagulation (not aspirin) is indicated — check rhythm history.", "Add PPI if prior GI bleed."],
      citations: ["AHA/ASA Secondary Prevention 2024"],
      rx: mkRx({ brand_name: "Ecosprin 75", generic_name: "Aspirin", strength: "75 mg", dose: "1 tab", frequency: "OD", timing: "After lunch" }),
    }));
  }
  if (!alreadyOn(meds, "atorvastatin", "rosuvastatin", "statin")) {
    suggestions.push(mkSug({
      regimen: "High-intensity statin",
      drug: "Atorvastatin 40 mg", dose: "40 mg HS",
      titration: "Target LDL < 70 mg/dL; recheck lipids at 6-8 weeks.",
      rationale: "SPARCL: high-intensity statin reduces recurrent stroke and MACE post-ischaemic stroke.",
      cautions: ["Check LFT at baseline; myalgia review at follow-up."],
      citations: ["SPARCL", "AHA/ASA 2024"],
      rx: mkRx({ brand_name: "Atorva 40", generic_name: "Atorvastatin", strength: "40 mg", dose: "1 tab", frequency: "OD", timing: "Bedtime" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// POST-BARIATRIC SUPPLEMENTATION — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestBariatric(ctx) {
  if (!hasDx(ctx, /post-bariatric/, /post bariatric/, /bariatric surgery/, /sleeve gastrectomy/, /gastric bypass/)) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (!alreadyOn(meds, "methylcobalamin", "cyanocobalamin", "b12")) {
    suggestions.push(mkSug({
      regimen: "Lifelong B12 replacement",
      drug: "Methylcobalamin 1500 mcg", dose: "1500 mcg OD sublingual",
      titration: "Check B12 annually; switch to monthly IM if levels stay low.",
      rationale: "Bariatric anatomy prevents intrinsic-factor-dependent B12 absorption — deficiency is near-universal without supplementation.",
      cautions: ["Annual labs: B12, ferritin, vitamin D, calcium, albumin."],
      citations: ["ASMBS Nutrition 2023"],
      rx: mkRx({ brand_name: "Methylcobal 1500", generic_name: "Methylcobalamin", strength: "1500 mcg", dose: "1 tab", frequency: "OD", timing: "Before breakfast", instructions: "Dissolve under tongue." }),
    }));
  }
  if (!alreadyOn(meds, "calcium citrate", "shelcal", "calcium")) {
    suggestions.push(mkSug({
      regimen: "Calcium citrate + D3",
      drug: "Calcium citrate 500 mg + Vitamin D3", dose: "1 tab BD (separate from iron by 2 hrs)",
      titration: "Citrate salt specifically — carbonate needs acid and is poorly absorbed post-bariatric.",
      rationale: "Prevents secondary hyperparathyroidism and bone loss after bariatric surgery.",
      cautions: ["Take 2 hours apart from iron/thyroxine."],
      citations: ["ASMBS Nutrition 2023"],
      rx: mkRx({ brand_name: "Shelcal-CT", generic_name: "Calcium citrate + Vitamin D3", strength: "500 mg/250 IU", dose: "1 tab", frequency: "BD", timing: "After meals" }),
    }));
  }
  if (!alreadyOn(meds, "ferrous", "iron")) {
    suggestions.push(mkSug({
      regimen: "Iron + folate maintenance",
      drug: "Ferrous ascorbate 100 mg + Folic acid", dose: "1 tab OD empty stomach",
      titration: "Menstruating women need this lifelong; check ferritin annually.",
      rationale: "Duodenal bypass removes the main iron absorption site.",
      cautions: ["Separate from calcium and thyroxine by 2 hours."],
      citations: ["ASMBS Nutrition 2023"],
      recommended: false,
      rx: mkRx({ brand_name: "Orofer XT", generic_name: "Ferrous ascorbate + Folic acid", strength: "100 mg/1.5 mg", dose: "1 tab", frequency: "OD", timing: "Empty stomach" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// URO-GYNAECOLOGY (OAB, ED, Recurrent candidiasis, AUB, Menopause) — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestUroGyn(ctx) {
  const oab = hasDx(ctx, /overactive bladder/, /incontinence/, /\boab\b/);
  const ed = hasDx(ctx, /erectile dysfunction/, /\bed\b(?!ema)/);
  const cand = hasDx(ctx, /vaginal candidiasis/, /recurrent candidiasis/, /vulvovaginal candid/);
  const aub = hasDx(ctx, /abnormal uterine bleeding/, /\baub\b/, /menorrhagia/);
  const meno = hasDx(ctx, /menopause/, /perimenopaus/);
  if (!oab && !ed && !cand && !aub && !meno) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (oab && !alreadyOn(meds, "mirabegron", "solifenacin", "darifenacin", "oxybutynin")) {
    suggestions.push(mkSug({
      regimen: "OAB — β3 agonist (anticholinergic-sparing)",
      drug: "Mirabegron 25 mg", dose: "25 mg OD, may increase to 50 mg at 4-8 weeks",
      titration: "Combine with bladder training + timed voiding. Solifenacin 5 mg is the alternative if cost is an issue.",
      rationale: "Mirabegron avoids the cognitive/dry-mouth burden of anticholinergics — preferred in elderly and diabetics.",
      cautions: ["Check BP — can raise it; avoid in uncontrolled HTN (≥180/110).", "Rule out UTI and retention (post-void residual) before labelling OAB."],
      citations: ["AUA/SUFU OAB 2024"],
      rx: mkRx({ brand_name: "Mirago 25", generic_name: "Mirabegron", strength: "25 mg", dose: "1 tab", frequency: "OD", timing: "Any time, consistent" }),
    }));
  }
  if (ed && !alreadyOn(meds, "tadalafil", "sildenafil", "vardenafil")) {
    suggestions.push(mkSug({
      regimen: "ED — PDE5 inhibitor",
      drug: "Tadalafil 5 mg", dose: "5 mg OD (daily) or 10 mg on-demand 30 min before",
      titration: "Daily 5 mg also treats coexisting BPH-LUTS. Review testosterone + screen CV risk — ED predicts CAD by ~3 years.",
      rationale: "PDE5 inhibitors are first-line; daily tadalafil suits regular activity + BPH overlap.",
      cautions: ["ABSOLUTE contraindication with nitrates (isosorbide, GTN) — verify med list.", "Caution with alpha-blockers (hypotension) — separate dosing.", "Screen fasting glucose/lipids — ED is a vascular red flag."],
      citations: ["AUA ED 2023"],
      rx: mkRx({ brand_name: "Megalis 5", generic_name: "Tadalafil", strength: "5 mg", dose: "1 tab", frequency: "OD", timing: "Same time daily" }),
    }));
  }
  if (cand && !alreadyOn(meds, "fluconazole")) {
    suggestions.push(mkSug({
      regimen: "Recurrent VVC — suppressive regimen",
      drug: "Fluconazole 150 mg", dose: "150 mg every 72 hrs × 3 doses, then 150 mg weekly × 6 months",
      titration: "Control blood sugars — recurrent candidiasis is uncontrolled-DM until proven otherwise (check HbA1c).",
      rationale: "Induction + 6-month weekly maintenance achieves >90% remission in recurrent VVC.",
      cautions: ["Avoid in pregnancy — use clotrimazole pessary instead.", "Check for drug interactions (warfarin, sulfonylureas — fluconazole potentiates both)."],
      citations: ["CDC STI/VVC 2024"],
      rx: mkRx({ brand_name: "Forcan 150", generic_name: "Fluconazole", strength: "150 mg", dose: "1 tab", frequency: "Weekly", duration: "6 months (after induction)", instructions: "First 3 doses 72 hrs apart, then weekly." }),
    }));
  }
  if (aub && !alreadyOn(meds, "tranexamic")) {
    suggestions.push(mkSug({
      regimen: "AUB — heavy-day control (while evaluating)",
      drug: "Tranexamic acid 500 mg", dose: "500 mg TDS only on heavy-flow days (max 5 days/cycle)",
      titration: "This is symptomatic cover ONLY — every AUB needs evaluation: USG pelvis + Hb; endometrial sampling if age > 40 or risk factors.",
      rationale: "Tranexamic acid reduces menstrual blood loss ~50% while workup proceeds.",
      cautions: ["Avoid in active thromboembolic disease or prior DVT/PE.", "Postmenopausal bleeding is NEVER treated symptomatically — refer for endometrial evaluation."],
      citations: ["FIGO AUB 2023"],
      rx: mkRx({ brand_name: "Pause 500", generic_name: "Tranexamic acid", strength: "500 mg", dose: "1 tab", frequency: "TDS", duration: "Heavy days only (max 5 days)", type: "acute" }),
    }));
  }
  if (meno && !alreadyOn(meds, "calcium", "shelcal")) {
    suggestions.push(mkSug({
      regimen: "Menopause — bone protection baseline",
      drug: "Calcium 500 mg + Vitamin D3 250 IU", dose: "1 tab OD after food",
      titration: "Order DEXA if ≥ 65 or risk factors; discuss MHT for severe vasomotor symptoms (specialist decision, within 10 yrs of menopause).",
      rationale: "Postmenopausal bone loss accelerates 2-3%/year in the first 5 years — calcium/D3 plus weight-bearing exercise is the floor.",
      cautions: ["MHT is individualised — do not start empirically if breast Ca / VTE / CVD history.", "Any postmenopausal bleeding → urgent endometrial evaluation."],
      citations: ["IMS 2024", "NAMS 2022"],
      rx: mkRx({ brand_name: "Shelcal 500", generic_name: "Calcium carbonate + Vitamin D3", strength: "500 mg/250 IU", dose: "1 tab", frequency: "OD", timing: "After dinner" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// GI SYMPTOMATIC (Constipation, Haemorrhoids, Coeliac, H. pylori hx) — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestGISymptomatic(ctx) {
  const constip = hasDx(ctx, /constipation/);
  const piles = hasDx(ctx, /haemorrhoid/, /hemorrhoid/, /fissure/, /\bpiles\b/);
  const coeliac = hasDx(ctx, /coeliac/, /celiac/, /gluten/);
  const hpylori = hasDx(ctx, /h\. ?pylori/, /helicobacter/);
  if (!constip && !piles && !coeliac && !hpylori) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (constip && !alreadyOn(meds, "lactulose", "isabgol", "psyllium", "polyethylene glycol")) {
    suggestions.push(mkSug({
      regimen: "Chronic constipation — stepped care",
      drug: "Isabgol (psyllium husk)", dose: "1-2 tsp in warm water at bedtime",
      titration: "Step 2 if inadequate: Lactulose 15 ml HS. Review medications causing constipation (iron, CCBs, opioids, anticholinergics).",
      rationale: "Soluble fibre first, osmotic laxative second — stimulants only for rescue.",
      cautions: ["Red flags (blood in stool, weight loss, new onset > 50 yrs) → colonoscopy referral.", "Needs adequate water intake (≥ 1.5 L) to work."],
      citations: ["Rome IV", "AGA CIC 2023"],
      rx: mkRx({ brand_name: "Naturolax", generic_name: "Isabgol (Psyllium)", strength: "—", dose: "1-2 tsp", dosage_form: "Powder", frequency: "HS", timing: "Bedtime with warm water" }),
    }));
  }
  if (piles) {
    suggestions.push(mkSug({
      regimen: "Haemorrhoids / fissure — conservative bundle",
      drug: "Sitz bath + topical anaesthetic-steroid + fibre", dose: "Warm sitz bath 10 min BD; apply cream BD × 7 days",
      titration: "Grade III-IV, persistent bleeding or thrombosis → surgical referral. Fix constipation (root cause) with fibre + fluids.",
      rationale: "Most grade I-II haemorrhoids settle with defecation-habit correction and short topical therapy.",
      cautions: ["Do not use steroid cream beyond 7-10 days (mucosal atrophy).", "Rectal bleeding in > 45 yrs still merits colonoscopy even with visible piles."],
      citations: ["ASCRS 2024"],
      rx: mkRx({ brand_name: "Anovate Cream", generic_name: "Beclometasone + Phenylephrine + Lidocaine", strength: "—", dose: "Pea-sized amount", route: "Per rectal", dosage_form: "Cream", frequency: "BD", duration: "7 days", type: "acute" }),
    }));
  }
  if (coeliac) {
    suggestions.push(mkSug({
      regimen: "Coeliac — strict gluten-free diet + deficiency screen",
      drug: "Lifelong gluten-free diet", dose: "Zero wheat/barley/rye — rice, millets (bajra/jowar/ragi) are safe",
      titration: "Screen and replace: iron, B12, folate, vitamin D, calcium at diagnosis and annually. Repeat tTG-IgA at 6-12 months to confirm adherence.",
      rationale: "GFD is the only treatment; mucosal recovery reverses the deficiencies.",
      cautions: ["Hidden gluten in sauces, atta mixes and processed foods — dietician referral recommended.", "First-degree relatives: 10% risk — offer screening."],
      citations: ["ACG Coeliac 2023"],
      recommended: false,
    }));
  }
  if (hpylori) {
    suggestions.push(mkSug({
      regimen: "H. pylori (treated) — confirm eradication",
      drug: "Urea breath test / stool antigen", dose: "≥ 4 weeks after antibiotics AND ≥ 2 weeks off PPI",
      titration: "If still positive → second-line levofloxacin-based triple or bismuth quadruple therapy.",
      rationale: "Eradication failure runs 20-30% with Indian clarithromycin resistance — always confirm.",
      cautions: ["Testing on PPI gives false negatives — stop PPI 2 weeks prior (H2 blocker cover is fine)."],
      citations: ["Maastricht VI"],
      recommended: false,
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// TOBACCO / ALCOHOL CESSATION — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestAddiction(ctx) {
  const smoke = hasDx(ctx, /smoking/, /tobacco/, /nicotine/);
  const alco = hasDx(ctx, /alcohol use/, /alcohol dependence/, /alcoholism/);
  if (!smoke && !alco) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (smoke && !alreadyOn(meds, "nicotine", "varenicline", "bupropion")) {
    suggestions.push(mkSug({
      regimen: "Tobacco cessation — NRT first line",
      drug: "Nicotine gum 2 mg (4 mg if > 20 cigarettes/day or first smoke < 30 min of waking)", dose: "1 gum q1-2h while awake (max 24/day), taper over 12 weeks",
      titration: "Set a quit date; combine with brief counselling every visit (doubles success). Varenicline 0.5→1 mg BD is the strongest single agent if NRT fails.",
      rationale: "NRT + counselling doubles quit rates; every consultation is a cessation opportunity (5A framework).",
      cautions: ["Chew-and-park technique — continuous chewing causes hiccups/dyspepsia.", "For smokeless (gutka/khaini) users the same NRT dosing applies."],
      citations: ["WHO MPOWER", "USPSTF 2021"],
      rx: mkRx({ brand_name: "Nicotex 2 mg", generic_name: "Nicotine gum", strength: "2 mg", dose: "1 gum", route: "Buccal", dosage_form: "Chewing gum", frequency: "q1-2h PRN (max 24/day)", duration: "12 weeks tapering", instructions: "Chew slowly till tingling, then park between cheek and gum. No food/drink 15 min before." }),
    }));
  }
  if (alco && !alreadyOn(meds, "thiamine", "benfotiamine")) {
    suggestions.push(mkSug({
      regimen: "Alcohol use — neuroprotection + brief intervention",
      drug: "Thiamine 100 mg", dose: "100 mg OD (BD if poor nutrition)",
      titration: "Brief intervention (FRAMES) every visit; consider referral for de-addiction. Anti-craving agents (baclofen/naltrexone/acamprosate) are specialist-initiated.",
      rationale: "Thiamine prevents Wernicke's encephalopathy — cheap, essential cover for every regular drinker.",
      cautions: ["Screen LFT, GGT, CBC (macrocytosis).", "Never start disulfiram without informed consent and supervision.", "Abrupt cessation in heavy dependence risks withdrawal seizures — plan supervised detox."],
      citations: ["NICE CG115"],
      rx: mkRx({ brand_name: "Benadon 100", generic_name: "Thiamine", strength: "100 mg", dose: "1 tab", frequency: "OD", timing: "After breakfast" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// URTICARIA + RECURRENT TINEA — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestUrticariaTinea(ctx) {
  const urt = hasDx(ctx, /urticaria/, /chronic hives/);
  const tinea = hasDx(ctx, /tinea/, /fungal infection/, /dermatophyt/, /ringworm/);
  if (!urt && !tinea) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (urt && !alreadyOn(meds, "bilastine", "fexofenadine", "levocetirizine", "cetirizine")) {
    suggestions.push(mkSug({
      regimen: "Chronic urticaria — 2nd-gen antihistamine",
      drug: "Bilastine 20 mg", dose: "20 mg OD; may up-dose to 2× standard if uncontrolled at 2-4 weeks",
      titration: "EAACI allows up to 4× standard antihistamine dose before biologics. Track with UAS7 score.",
      rationale: "Non-sedating H1 blockade is first-line; most CSU controls with up-dosing alone.",
      cautions: ["Avoid systemic steroids for maintenance — rebound and side-effects.", "Screen thyroid antibodies — autoimmune association."],
      citations: ["EAACI/GA²LEN Urticaria 2024"],
      rx: mkRx({ brand_name: "Bilaxten 20", generic_name: "Bilastine", strength: "20 mg", dose: "1 tab", frequency: "OD", timing: "Empty stomach" }),
    }));
  }
  if (tinea && !alreadyOn(meds, "itraconazole", "terbinafine", "luliconazole", "griseofulvin")) {
    suggestions.push(mkSug({
      regimen: "Recurrent tinea — adequate systemic + topical course",
      drug: "Itraconazole 100 mg", dose: "100 mg BD × 2-4 weeks + Luliconazole cream OD × 4 weeks",
      titration: "Treat 2 weeks beyond clinical clearance; treat all household contacts and disinfect clothing/bedding to stop the reinfection cycle.",
      rationale: "Indian recurrent tinea is driven by steroid-cream misuse and under-dosing — adequate azole course + strict no-steroid rule breaks recurrence.",
      cautions: ["NEVER prescribe/permit steroid-antifungal combo creams (Quadriderm etc.) — the main driver of resistant tinea.", "Itraconazole: caution with statins (myopathy — consider pausing), avoid in heart failure; check LFT if > 4 weeks.", "Take with fatty food/cola for absorption."],
      citations: ["IADVL Tinea Consensus 2023"],
      rx: mkRx({ brand_name: "Canditral 100", generic_name: "Itraconazole", strength: "100 mg", dose: "1 cap", frequency: "BD", timing: "After fatty meal", duration: "2-4 weeks" }),
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// ALLERGIC BRONCHITIS + POST-TB SEQUELAE — NEW 2026-08
// -----------------------------------------------------------------------------
export function suggestAirwayOther(ctx) {
  const ab = hasDx(ctx, /allergic bronchitis/);
  const posttb = hasDx(ctx, /post-tb/, /post tb sequelae/, /tb sequelae/, /post-tubercul/);
  if (!ab && !posttb) return { recommend: false, suggestions: [] };
  const meds = ctx.currentMeds || [];
  const suggestions = [];
  if (ab && !alreadyOn(meds, "montelukast", "levocetirizine")) {
    suggestions.push(mkSug({
      regimen: "Allergic airway — LTRA + antihistamine",
      drug: "Montelukast 10 mg + Levocetirizine 5 mg", dose: "1 tab HS",
      titration: "Reassess at 4-6 weeks; frequent symptoms or wheeze → treat as asthma (add ICS, do spirometry).",
      rationale: "Controls allergic cough/bronchospasm; also covers coexisting allergic rhinitis.",
      cautions: ["Counsel on rare montelukast neuropsychiatric effects (vivid dreams, mood change).", "Recurring 'allergic bronchitis' in adults is often undiagnosed asthma — have a low threshold for spirometry."],
      citations: ["ARIA 2024", "GINA 2025"],
      rx: mkRx({ brand_name: "Montair-LC", generic_name: "Montelukast + Levocetirizine", composition: "Montelukast 10 mg + Levocetirizine 5 mg", strength: "10/5 mg", dose: "1 tab", frequency: "HS", timing: "Bedtime" }),
    }));
  }
  if (posttb) {
    suggestions.push(mkSug({
      regimen: "Post-TB lung disease — maintenance care",
      drug: "Vaccination + airway clearance + bronchodilator PRN", dose: "Annual influenza + pneumococcal (PCV13→PPSV23) vaccines",
      titration: "Spirometry to phenotype (obstructive/restrictive); SABA or LAMA if obstructive component. Recurrent haemoptysis → CT chest (rule out aspergilloma/bronchiectasis).",
      rationale: "Post-TB sequelae behave like COPD/bronchiectasis — vaccination and airway clearance cut exacerbations.",
      cautions: ["New constitutional symptoms or haemoptysis → rule out TB relapse (sputum CBNAAT) before labelling sequelae."],
      citations: ["WHO Post-TB Care 2024"],
      recommended: false,
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

// -----------------------------------------------------------------------------
// SAFETY-FLAG ADVISORIES (advice-only, no drug push) — NEW 2026-08
// Glaucoma, Dialysis/Transplant, Pulm HTN, Recurrent hypoglycaemia, Bipolar/
// Schizophrenia on Rx, Goitre, Retinopathy, Fibroid, Infertility on Rx.
// -----------------------------------------------------------------------------
export function suggestSafetyFlags(ctx) {
  const suggestions = [];
  if (hasDx(ctx, /glaucoma/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Glaucoma — prescribing guard",
      drug: "Continue ophthalmologist's drops; screen every new Rx", dose: "—",
      titration: "Ensure annual IOP/fields follow-up with ophthalmology.",
      rationale: "Several common drugs can precipitate angle closure or raise IOP.",
      cautions: ["AVOID / use with caution: systemic & topical steroids (raise IOP), anticholinergics (oxybutynin, TCAs, first-gen antihistamines), decongestants, and topiramate (acute angle closure)."],
      citations: ["AAO PPP 2024"], recommended: false,
    }));
  }
  if (hasDx(ctx, /on dialysis/, /renal transplant/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Dialysis / Transplant — renal prescribing guard",
      drug: "Coordinate every new drug with nephrologist", dose: "—",
      titration: "Time doses AFTER dialysis sessions for dialysable drugs.",
      rationale: "Dose adjustment and interaction screening are mandatory; transplant immunosuppressants (tacrolimus) have narrow margins.",
      cautions: ["NEVER: NSAIDs, nephrotoxic antibiotics without dose adjustment.", "Transplant: azoles, macrolides, diltiazem swing tacrolimus levels — check DDI every time.", "Avoid live vaccines in transplant recipients."],
      citations: ["KDIGO Transplant 2023"], recommended: false,
    }));
  }
  if (hasDx(ctx, /pulmonary hypertension/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Pulmonary hypertension — specialist domain",
      drug: "PAH-specific therapy only via cardiology/pulmonology", dose: "—",
      titration: "6-min walk test + echo PASP at follow-ups.",
      rationale: "PAH drugs (sildenafil for PAH, bosentan) need right-heart-cath-confirmed diagnosis.",
      cautions: ["Avoid decongestants and beta-agonist overuse.", "Diuretics for RV overload only with careful volume assessment.", "Pregnancy is high-risk — contraception counselling essential."],
      citations: ["ESC/ERS PH 2022"], recommended: false,
    }));
  }
  if (hasDx(ctx, /hypoglycaemia \(recurrent\)/, /recurrent hypoglycaemia/, /recurrent hypoglycemia/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Recurrent hypoglycaemia — regimen review",
      drug: "De-intensify: review sulfonylurea/insulin doses NOW", dose: "—",
      titration: "Relax HbA1c target (7.5-8% if elderly/hypo-unaware); switch SU → DPP4i/SGLT2i; consider CGM.",
      rationale: "Recurrent hypos cause arrhythmia, falls and hypoglycaemia-unawareness — usually an over-treatment signal.",
      cautions: ["Educate: 15-15 rule (15 g fast carb, recheck in 15 min); keep glucose sachets.", "Screen for renal decline — insulin/SU clearance falls with eGFR.", "Driving counselling: check sugar before driving."],
      citations: ["ADA 2026 §6"], recommended: false,
    }));
  }
  if (hasDx(ctx, /bipolar/, /schizophrenia/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Serious mental illness on Rx — interaction & metabolic guard",
      drug: "Do NOT stop/modify psychiatric drugs; screen interactions", dose: "—",
      titration: "Annual metabolic screen: weight, FBS/HbA1c, lipids (antipsychotics are diabetogenic). Lithium: 6-monthly levels + TSH + creatinine.",
      rationale: "Abrupt psychotropic changes precipitate relapse; many antipsychotics prolong QT and worsen metabolic profile.",
      cautions: ["Lithium + NSAIDs/ACEi/thiazides → toxicity.", "Avoid tramadol/linezolid with SSRIs (serotonin syndrome).", "QT caution when adding azithromycin/ondansetron/fluoroquinolones."],
      citations: ["Maudsley Guidelines 15th"], recommended: false,
    }));
  }
  if (hasDx(ctx, /goitre/, /thyroid nodule/)) {
    suggestions.push(mkSug({
      regimen: "Goitre / nodule — structured workup",
      drug: "TSH + USG thyroid (TIRADS)", dose: "—",
      titration: "TIRADS 4-5 or > 1-2 cm → FNAC. Toxic features → uptake scan.",
      rationale: "Most nodules are benign; sonographic risk-stratification prevents both missed cancers and needless surgery.",
      cautions: ["Rapid growth, hoarseness, hard fixed nodule, cervical nodes → urgent referral.", "Do NOT start empirical thyroxine to 'shrink' nodules."],
      citations: ["ATA Nodule 2023"], recommended: false,
    }));
  }
  if (hasDx(ctx, /diabetic retinopathy/)) {
    suggestions.push(mkSug({
      regimen: "Diabetic retinopathy — sight-saving schedule",
      drug: "Ophthalmology follow-up + risk-factor triple control", dose: "Dilated fundus exam: annually (mild), 3-6 monthly if moderate+",
      titration: "Tight BP (< 130/80) and lipid control slow progression; avoid very rapid HbA1c drops (> 2%/3 months) in advanced DR.",
      rationale: "DR is the leading cause of working-age blindness — progression is largely preventable.",
      cautions: ["Sudden floaters/vision loss → same-day ophthalmology (vitreous haemorrhage).", "Fenofibrate has DR-progression benefit (ACCORD-Eye) — consider if dyslipidaemic."],
      citations: ["ADA 2026 §12", "ACCORD-Eye"], recommended: false,
    }));
  }
  if (hasDx(ctx, /fibroid/)) {
    suggestions.push(mkSug({
      regimen: "Fibroid uterus — surveillance",
      drug: "USG pelvis 6-12 monthly + Hb monitoring", dose: "—",
      titration: "Heavy bleeding → tranexamic acid on heavy days ± hormonal options (gynaecologist); rapid growth or postmenopausal growth → urgent referral.",
      rationale: "Most fibroids need only surveillance; treatment is symptom-driven.",
      cautions: ["Correct iron deficiency from chronic bleeding (check ferritin)."],
      citations: ["FIGO 2023"], recommended: false,
    }));
  }
  if (hasDx(ctx, /infertility \(under rx\)/, /infertility under/)) {
    suggestions.push(mkSug({
      regimen: "⚠ Infertility under treatment — pregnancy-safe prescribing",
      drug: "Treat every cycle as a possible early pregnancy", dose: "—",
      titration: "Coordinate any new chronic drug with the fertility specialist.",
      rationale: "Ovulation induction cycles mean conception may predate the next visit — teratogen screening applies NOW.",
      cautions: ["Avoid: ACEi/ARB, statins, methotrexate, tetracyclines, fluoroquinolones, NSAIDs around ovulation/implantation.", "Start Folic acid 5 mg OD if not already on it."],
      citations: ["RCOG Preconception 2024"], recommended: false,
    }));
  }
  return { recommend: suggestions.length > 0, suggestions };
}

export const CHRONIC_ORDER = [
  { key: "respiratory", label: "Respiratory (Asthma / COPD)", fn: suggestRespiratory },
  { key: "gout", label: "Gout / Hyperuricemia", fn: suggestGout },
  { key: "osteoporosis", label: "Osteoporosis / Fragility Fracture", fn: suggestOsteoporosis },
  { key: "psych", label: "Depression / Anxiety", fn: suggestPsych },
  { key: "epilepsy", label: "Epilepsy / Seizure Disorders", fn: suggestEpilepsy },
  { key: "migraine", label: "Migraine / Chronic Headache", fn: suggestMigraine },
  { key: "neuropathy", label: "Neuropathic Pain / Diabetic Neuropathy", fn: suggestNeuropathy },
  { key: "chronic_pain", label: "Chronic Pain / Osteoarthritis", fn: suggestChronicPain },
  { key: "pcos", label: "PCOS / Metabolic Infertility", fn: suggestPCOS },
  { key: "bph", label: "BPH / LUTS", fn: suggestBPH },
  { key: "af", label: "Atrial Fibrillation (AF)", fn: suggestAF },
  { key: "liver", label: "NAFLD / Chronic Liver Disease", fn: suggestLiverMetabolic },
  { key: "hematology", label: "Anemia / Hematology", fn: suggestHematology },
  { key: "derm", label: "Dermatology (Eczema / Psoriasis)", fn: suggestDermatology },
  { key: "obesity", label: "Obesity / Metabolic Syndrome", fn: suggestObesity },
  { key: "hyperthyroid", label: "Hyperthyroidism / Thyrotoxicosis", fn: suggestHyperthyroid },
  { key: "rheuma_biologic", label: "Rheumatology (RA / AS / SLE / PsA)", fn: suggestRheumatologyBiologic },
  { key: "venous", label: "Chronic Venous Insufficiency", fn: suggestVenous },
  { key: "pancreatitis", label: "Chronic Pancreatitis", fn: suggestChronicPancreatitis },
  { key: "ruti", label: "Recurrent UTI", fn: suggestRecurrentUTI },
  { key: "bronchiectasis", label: "Bronchiectasis", fn: suggestBronchiectasis },
  { key: "liver_panel", label: "ALD / Hepatitis C / Cirrhosis", fn: suggestLiverPanel },
  { key: "stones", label: "Gallstones / Nephrolithiasis", fn: suggestGallstoneStone },
  { key: "endo_rare", label: "Addison's / Prolactin / RHD", fn: suggestEndocrineRare },
  { key: "ibd", label: "IBD (UC / Crohn's)", fn: suggestIBD },
  { key: "fibromyalgia", label: "Fibromyalgia", fn: suggestFibromyalgia },
  { key: "neurodegen", label: "Parkinson's / Dementia", fn: suggestNeuroDegen },
  { key: "insomnia", label: "Insomnia", fn: suggestInsomnia },
  { key: "ent_allergy", label: "Chronic Sinusitis / Allergic Rhinitis", fn: suggestENTAllergy },
  { key: "spine_pain", label: "Spondylosis / Frozen Shoulder / Plantar Fasciitis", fn: suggestSpinePain },
  { key: "vertigo_tn", label: "Vertigo (BPPV) / Trigeminal Neuralgia", fn: suggestVertigoNeuralgia },
  { key: "post_cva", label: "Post CVA / Stroke — Secondary Prevention", fn: suggestPostCVA },
  { key: "bariatric", label: "Post-Bariatric Supplementation", fn: suggestBariatric },
  { key: "urogyn", label: "OAB / ED / Candidiasis / AUB / Menopause", fn: suggestUroGyn },
  { key: "gi_symptomatic", label: "Constipation / Piles / Coeliac / H. pylori", fn: suggestGISymptomatic },
  { key: "addiction", label: "Tobacco / Alcohol Cessation", fn: suggestAddiction },
  { key: "urticaria_tinea", label: "Chronic Urticaria / Recurrent Tinea", fn: suggestUrticariaTinea },
  { key: "airway_other", label: "Allergic Bronchitis / Post-TB Sequelae", fn: suggestAirwayOther },
  { key: "safety_flags", label: "Safety Advisories (Glaucoma / Dialysis / SMI…)", fn: suggestSafetyFlags },
];

export function suggestAllChronicConditions(ctx) {
  const out = {};
  for (const { key, fn } of CHRONIC_ORDER) {
    try { out[key] = fn(ctx); }
    catch (e) { out[key] = { recommend: false, suggestions: [], error: String(e?.message || e) }; }
  }
  return out;
}
